import type { BookLiquiditySeed } from '@/stores/bookLiquiditySettings';
import { US_GAME_SPORTS, usGameSportForBoard, type UsGameSport } from '@/lib/polymarketUs/sports';
import { useState, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { observedVolume } from '@/lib/market/tickerScalars';
import { useSharedNow } from '@/lib/infra/sharedClock';
import type { GameEventRow, GameRow, Sport, MarketType, DateFilter, SlateRange, MarketSide, TeamSide, MultiOutcomeEvent, SeriesOutcome, TradeTicketContext } from './types';
import { useNovigVenueSeam } from '@/lib/novig/seam';
import type { NovigBoardClick } from './novig/NovigBoardCell';
import { CFB_BUCKET_LABEL, STANDALONE_SPORTS, MULTI_OUTCOME_SPORTS, SPORT_LABEL, SPORT_TABS } from './types';

/** #1232 — how often the board re-offers its US lookups (a miss is retried
 * once its 5 min window has passed, so within 6 min). */
const US_BOARD_LOOKUP_RETRY_TICK_MS = US_MARKET_MISS_RETRY_MS / 5;

function mdyToIso(mdy: string): string {
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * #826 — why an empty board is empty, and one click out of it.
 *
 * A board resumed from the trader's last visit can legitimately come back with
 * nothing on it: Settled markets on a date that has none. Without this the
 * empty state reads as "no such markets exist" when the real answer is a
 * filter the trader set before they walked away. Deliberately quiet — one
 * muted line under the existing copy, and only while a reading filter is
 * actually narrowing the board, so an ordinary empty slate gains no clutter
 * and nothing here is shaped like a warning.
 *
 * "Clear these", NOT the toolbar's "Reset view", and the difference is the
 * point. What the trader wants back here is their GAMES; their board, tab,
 * side and price mode are choices they made deliberately and that an empty
 * result says nothing about. So this clears exactly the filters the line
 * names and nothing else — the label and the effect are the same list. The
 * toolbar's Reset stays the whole-view reset for when that is what is wanted.
 */
function ReadingFilterHint({ summary, onClear }: { summary: string | null; onClear: () => void }) {
  if (!summary) return null;
  return (
    <span className="text-xs">
      {summary} &mdash;{' '}
      <button
        type="button"
        onClick={onClear}
        className="text-primary underline-offset-2 hover:underline"
      >
        Clear these
      </button>
    </span>
  );
}

function gameSortMs(game: GameRow): number {
  if (game.startTimeMs != null && Number.isFinite(game.startTimeMs)) {
    return game.startTimeMs;
  }
  const dayMs = startOfIsoDayMs(game.date);
  return Number.isFinite(dayMs) ? dayMs : Number.POSITIVE_INFINITY;
}

const SPORT_ORDER = new Map<Sport, number>(SPORT_TABS.map((tab, index) => [tab.id, index]));

function sortSports(sports: Iterable<Sport>): Sport[] {
  return Array.from(sports).sort((a, b) => (SPORT_ORDER.get(a) ?? 999) - (SPORT_ORDER.get(b) ?? 999));
}

const subscribeAuthEpoch = (notify: () => void) => useAuthStore.subscribe(notify);
const subscribePageVisibility = (notify: () => void) => {
  document.addEventListener('visibilitychange', notify);
  return () => document.removeEventListener('visibilitychange', notify);
};
const readPageVisibility = () => document.visibilityState === 'visible';

function sportSelectionLabel(sports: Sport[], allSports: boolean): string {
  if (allSports) return 'All Sports';
  if (sports.length === 1) return SPORT_LABEL[sports[0]] ?? sports[0].toUpperCase();
  if (sports.length === 2) return sports.map((sport) => SPORT_LABEL[sport] ?? sport.toUpperCase()).join(' + ');
  return `${sports.length} Sports`;
}

/** The #248 focus contract's own query keys, in a fixed order.
 *
 *  The dashboard URL carries other things (today just `sport`, tomorrow
 *  whatever a future feature adds), and the parsed focus `identity` is the
 *  one-shot key for hydration — so an unrelated param must never be able to
 *  reset it. Projecting the query through this list, in THIS order, makes the
 *  identity depend on the focus contract alone and on nothing else, whatever
 *  order the keys arrived in. Repeats are preserved deliberately: a repeated
 *  focus key is a contradiction the parser has to see and reject. */
const FOCUS_QUERY_KEYS = ['focus', 'sport', 'date', 'event', 'ticker', 'venue', 'side', 'reason'] as const;

function focusedQueryString(params: URLSearchParams): string {
  const focused = new URLSearchParams();
  for (const key of FOCUS_QUERY_KEYS) {
    for (const value of params.getAll(key)) focused.append(key, value);
  }
  return focused.toString();
}

/** Handed to the hydration hook once the trader has deliberately taken the
 *  board somewhere else — see `focusTakenOver`. */
const NO_FOCUS: ParsedDashboardFocus = { kind: 'none' };

/** Which board a focused link's target lives on (#539).
 *
 *  Derived from the TICKER rather than read from a query key, deliberately. The
 *  ticker already determines the market's family, so a `kind=` parameter would
 *  be a second spelling of the same fact and the two could disagree — a link
 *  claiming `kind=total` for a moneyline ticker would land on a board that can
 *  never contain it. This mirrors Game Detail, whose `?ticker=&side=` deep link
 *  likewise names no kind and lets each section recognize its own markets. */
function focusedBoardFor(ticker: string): MarketType {
  return marketKindFromTicker(ticker) === 'moneyline' ? 'moneyline' : 'spread-total';
}

import {
  formatSlateRange,
  isSlateDate,
  isoToMdy,
  slateWindow,
  slateWindowContains,
  slateWindowDates,
  shiftSlate,
  startOfIsoDayMs,
  weeklySlateEligible,
  type SlateWindow,
} from './weeklySlate';
import {
  pickUpcomingSlate, slateScopeCopy, upcomingSlateCopy, upcomingSlateSearchCopy,
} from './upcomingSlate';
import { DashboardToolbar } from './DashboardToolbar';
import { FuturesDetailPanel } from './FuturesDetailPanel';
import { StandaloneMarketDetailPanel } from './FuturesDetailPanel';
import { GolfMatchBoards } from './GolfMatchBoards';
import type { StandaloneSelection } from './futuresDetail';
import { filterFutureBoards, futuresRows, futuresStatusMatches, isPolyFutureBoard, type FuturesBoard, type FuturesSelection } from './futuresDetail';
import { GameTable, NO_HIDDEN_EMPTY_SPORTS } from './GameTable';
import { LT2_DASH_ML_CARD_SCOPE } from './lt2Moneyline';
import { createGameRowIdentityStore } from './stableGameRows';
import { SeriesTable } from './SeriesTable';
import { MultiOutcomeTable } from './MultiOutcomeTable';
import {
  calendarDateInZone, commitDiscoveryQuery, discoveryRangeForDay, selectDiscoveryGroups,
  type DiscoveryMode,
} from './discovery';
import { buildDiscoveryCatalog } from './discoveryCatalog';
import {
  DISCOVERY_PAGE_SIZE, DISCOVERY_SPORTS, DISCOVERY_UNSUPPORTED_NOTE, DISCOVERY_WEEK,
  discoveryCatalogPlan,
  discoveryCoverage, discoveryReadingFor, isDiscoveryActive, isModeEligibleMarketType,
  isSearchableMarketType, splitDiscoveryResults,
} from './discoverySearch';
import { DiscoverySearchBar } from './DiscoverySearchBar';
import { DiscoveryModeControls } from './DiscoveryModeControls';
import { SpreadsTotals } from '../SpreadsTotals';
import { executableEvents, useHidePolyOnlyLines } from '../SpreadsTotals/polyOnlyLines';
import { synthEventKey } from '../SpreadsTotals/synthesize';
import { GameDetailPanel, type TradeIntent, type BetLineContext } from './GameDetailPanel';
import { advanceReplacesResult, buildMultiOutcomeBetContext, synthesizeFromMultiOutcome } from './multiOutcome';
import { bestPriceCentsForOrderSide } from './tradeModalBook';
import { PositionSidebar, PositionsWidget } from './PositionSidebar';
import { useMarketStore, useConsensusFor, useConsensusTrendFor, useOpOddsFor, useOpTrendFor, useOpLastAnchorFairFor } from '../../stores/marketStore';
import { buildOpFairView } from '../../lib/opOdds';
import { sportIsServable } from '../../clients/feedConfig';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { useWorkingScroll } from '@/hooks/useWorkingScroll';
import {
  dashboardReading, dashboardReadingFromUrl, resolveDateAnchor, toDateAnchor, todayIso,
} from './readingView';
import { useAccountStore } from '../../stores/accountStore';
import { usePolyAccountStore } from '../../stores/polyAccountStore';
import { usePolymarketRelayOrders } from '@/hooks/usePolymarketRelayOrders';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import {
  activityEvidenceIsAuthoritative,
  polyActivityPollingEnabled,
} from './emptyTennisEvidence';
import { useUsRail } from './useUsRail';
import { usActivityIsAuthoritative, usBoardActivity, usExposureLookups } from './usBoardActivity';
import { US_MARKET_MISS_RETRY_MS, UsMarketClassifier } from '@/lib/polymarketUs/marketFamily';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';

import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { useUsBoardQuotes } from './us/useUsBoardQuotes';
import { useUsAccountPresence } from '@/hooks/useUsAccountPresence';
import type { UsTradeClick } from './GameTable';
import {
  useSavedGames,
  gameMarkId,
  collapsedGameKey,
  isRowSaved,
  selectOwnedGames,
  type SavedGame,
} from '@/stores/savedGames';
import { useMinimizedGames, isRowMinimized } from '@/stores/minimizedGames';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { QueueVintageBadge } from '@/components/atoms/QueueVintageBadge';
import { useEdgeBotsByTicker } from '@/hooks/useEdgeBotsByTicker';
import { useArchivedGames, type ArchivedGame } from './useArchivedGames';
import { usePositionGameKeys } from './usePositionGameKeys';
import {
  baseGameKey,
  toHydraGameKey,
  toHydraGameKeyLive,
} from '@/services/analyticsGameKey';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { useFeedFreshness } from '@/hooks/useFeedFreshness';
import {
  parseFocusedDashboardQuery,
  type ParsedDashboardFocus,
} from '@/lib/routing/focusedDashboard';
import {
  ATP_CHALLENGER_DASHBOARD_SPORT,
  cfbBucketOf,
  dashboardSportMatches,
  isAtpChallengerRow,
  underlyingSport,
  type DashboardSport,
} from '@/lib/routing/dashboardSport';
import { readCfbLandingBoard } from '@/lib/routing/cfbLandingBoard';

/**
 * The board a bare `/dashboard` opens on, for one signed-in trader.
 *
 * A saved CFB landing board (#728) REFINES a saved default sport of 'cfb'; it
 * never makes CFB the default. Signed out, or with nothing chosen, this is the
 * saved sport exactly as before.
 */
function resolveLandingBoard(userId: string | null): DashboardSport {
  const sport = useDisplaySettings.getState().defaultSport;
  return sport === 'cfb' ? readCfbLandingBoard(userId) : sport;
}
import {
  useFocusedDashboardHydration,
  type UseFocusedDashboardHydrationInput,
  type ExternalFocusResolution,
} from './useFocusedDashboardHydration';
import { marketKindFromTicker } from '../PositionTracker/marketKind';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  tryOneClickFromMoneylineCtx,
  tryOneClickFromMultiOutcomePrice,
  tryOneClickFromTicketCtx,
} from './oneClickIntercept';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { useBoardFeedNeeds, VALUE_DASHBOARD_FEED_SOURCE } from '@/hooks/useBoardFeedNeeds';
import { useOddsFeedNeeds } from '@/hooks/useFeedNeeds';
import { useFeedSettlingState } from '@/hooks/useFeedSettling';
import type { FeedSettlingState } from '@/lib/feedFilter/feedSettling';
import { usPillPrefill } from './usPillPrefill';
import { OneClickToolbarCell } from './OneClickToolbarCell';
import { useOneClickRevocation } from './useOneClickRevocation';
import { useLt2ReadoutSlotShown } from '@/components/Lt2TargetReadout';
import {
  DISARM_REASON, ONE_CLICK_REVOKED_REASON, changedContextReason, disarmIfArmed, ticketClickReason,
  type DisarmContextField,
} from './oneClickDisarmReasons';
import type { BoardReading } from '@/lib/feedFilter/boardLines';
import { BROWSER_CADENCE_MS, type FeedNeeds } from '@/workers/feedFilter';
/** The board's markets cadence while no game is focused (g1xl-v2#1053 Part 2d). */
const BOARD_IDLE_CADENCE_MS = 1000;

interface ValueDashboardProps {
  /** Lets the floating positions popup open the full Position Tracker tab. */
  onOpenPositionsPage?: () => void;
}

// Activity-window for the chip-change resnap heal. Passed to
// requestResnapshotIfStale, which fires a snapshot only when the share
// of tickers with any frame in the last CHIP_RESNAP_STALE_MS falls below
// ~10% — i.e. the feed is mostly-dead, not just one chatty ticker
// keeping the global timestamp warm. 60 s window so quiet-but-healthy
// markets still register as active.
const CHIP_RESNAP_STALE_MS = 60_000;

/** D34 — the feed for this board has not answered, or refused its filter. */
function feedUnanswered(state: FeedSettlingState): boolean {
  return state.kind === 'unanswered' || state.kind === 'rejected';
}

/** D34 — an empty board whose feed has not answered says so, in amber,
 *  instead of "No events": the events may exist. */
function FeedUnansweredLine({ state, sportLabel }: { state: FeedSettlingState; sportLabel: string }) {
  return (
    <span
      role="status"
      className="text-amber-400 light:text-amber-700"
      title="Board rows come from the engine serving this sport; 'No events' appears only once it has answered."
    >
      {state.kind === 'rejected'
        ? `${sportLabel} feed rejected the board filter (${state.error})`
        : `No ${sportLabel} data: the ${sportLabel} feed isn't answering`}
    </span>
  );
}

export function ValueDashboard({ onOpenPositionsPage }: ValueDashboardProps = {}) {
  // ── Hydra data (connection managed at App level) ───────────────────
  // Feed freshness still drives the empty-state "Connecting to Hydra…" copy;
  // the stale/seconds readout moved to the global StatusBar.
  const { connected } = useFeedFreshness();
  const getGames = useMarketStore((s) => s.getGames);
  const getMultiOutcomeEvents = useMarketStore((s) => s.getMultiOutcomeEvents);
  // #754 — discovery's spread/total read. `SpreadsTotals` keeps its own read
  // for the ordinary board; this one exists because a search or a mode spans
  // sports, which that component is not asked to know about.
  const getGameEvents = useMarketStore((s) => s.getGameEvents);
  const getAdvanceEvents = useMarketStore((s) => s.getAdvanceEvents);
  const getSeriesEvents = useMarketStore((s) => s.getSeriesEvents);
  const getFutureBoards = useMarketStore((s) => s.getFutureBoards);
  const getFutureSports = useMarketStore((s) => s.getFutureSports);
  const getGolfMatches = useMarketStore((s) => s.getGolfMatches);
  const getActiveSports = useMarketStore((s) => s.getActiveSports);
  const requestResnapshotIfStale = useMarketStore((s) => s.requestResnapshotIfStale);
  // #730 — the dashboard no longer subscribes to the global `version` clock,
  // which moved on every publication for every sport. Each derivation keys on
  // the narrowest store signal that can change it:
  //   - boardRevKey (defined beside the board derivations): the per-sport
  //     revisions of exactly the sports on the board;
  //   - atpRev: the ATP Challenger chip reads ATP inventory whatever the board;
  //   - activeSportsKey: tab membership (active + future sport sets, identity);
  //   - gameIdentityVersion: ticker→game identity for the position game keys.
  // An unrelated sport's frame changes none of them, so it re-renders nothing
  // here and re-runs no `getGames` (AC-1).
  const atpRev = useMarketStore((s) => s.revBySport.atp ?? 0);
  const gameIdentityVersion = useMarketStore((s) => s.gameIdentityVersion);
  const activeSportsKey = useMarketStore(
    (s) => `${[...s.getActiveSports()].sort().join(',')}|${[...s.getFutureSports()].sort().join(',')}|${s.gameIdentityVersion}`,
  );

  // ── Saved games (#120) ─────────────────────────────────────────────
  // Browser-local, per-user. The list can't be read at module load because
  // we may not know who is signed in yet, so point the store at the current
  // user here and re-point it whenever that changes (login, logout, or a
  // cross-tab logout adopted by authStore's storage listener). Passing null
  // clears the in-memory list without erasing what's persisted, so signing
  // back in restores it.
  const authUserId = useAuthStore((s) => s.user?.id ?? null);
  const authOrgId = useAuthStore((s) => s.user?.orgId ?? null);
  const hydrateSavedGames = useSavedGames((s) => s.hydrateFor);
  useEffect(() => {
    hydrateSavedGames(authUserId);
  }, [authUserId, hydrateSavedGames]);
  const savedIds = useSavedGames((s) => s.ids);
  const savedCollapsedIds = useSavedGames((s) => s.collapsedIds);
  const savedOwnerId = useSavedGames((s) => s.userId);
  const savedIndex = useMemo(
    () => ({ userId: savedOwnerId, ids: savedIds, collapsedIds: savedCollapsedIds }),
    [savedOwnerId, savedIds, savedCollapsedIds],
  );
  // Ownership-gated: between a logout and the next hydration the loaded list
  // belongs to nobody, and this yields empty rather than the last user's games.
  const savedEntries = useSavedGames((s) => selectOwnedGames(s, authUserId));
  const removeSavedGame = useSavedGames((s) => s.remove);
  // Total saved across all sports/dates — distinguishes "you haven't saved
  // anything" from "your saved games just aren't on this tab", which the
  // filter-respecting behavior above makes a real question for the user.
  const savedCount = savedEntries.length;

  // ── Minimized games (#654) ─────────────────────────────────────────
  // Same per-user lifecycle as the saved list above, and for the same reason:
  // the list can't be read at module load, and a list belonging to the
  // previous account must never reach the screen.
  const hydrateMinimizedGames = useMinimizedGames((s) => s.hydrateFor);
  useEffect(() => {
    hydrateMinimizedGames(authUserId);
  }, [authUserId, hydrateMinimizedGames]);
  const minimizedIds = useMinimizedGames((s) => s.ids);
  const minimizedCollapsedIds = useMinimizedGames((s) => s.collapsedIds);
  const minimizedOwnerId = useMinimizedGames((s) => s.userId);
  const minimizedIndex = useMemo(
    () => ({
      userId: minimizedOwnerId,
      ids: minimizedIds,
      collapsedIds: minimizedCollapsedIds,
    }),
    [minimizedOwnerId, minimizedIds, minimizedCollapsedIds],
  );
  const restoreAllMinimized = useMinimizedGames((s) => s.restoreAll);
  const toggleMinimizedGame = useMinimizedGames((s) => s.toggle);

  // Landing-page defaults — read non-reactively so changing the pref in
  // Settings doesn't yank the active view out from under the user. They feed
  // the working view's defaults (#826), which only decide a board with nothing
  // remembered for it: the first paint of a visit, and a Reset. Every later
  // render reads the registered record instead, so the guarantee is unchanged.
  const initialMarketType = useDisplaySettings.getState().defaultMarketType;
  const initialPriceMode = useDisplaySettings.getState().defaultPriceMode;

  // ── Toolbar state ──────────────────────────────────────────────────
  // The sport filter lives in the URL (?sport=nba | ?sport=all; absent →
  // the user's saved default). Reload keeps you where you were, links are
  // shareable, and Back from GameDetail restores the sport natively through
  // history — this replaced the sessionStorage returnSport stash (TTL +
  // StrictMode peek/clear dance, deleted). Chip clicks write with
  // replace:true so they don't pile up history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  // The board a URL with no `sport` key lands on. A saved CFB landing board
  // (#728) refines a saved default sport of 'cfb'; every other sport is
  // itself.
  //
  // Re-seeded when the SIGNED-IN ACCOUNT changes, and only then — during
  // render, React's "adjusting state when a prop changes". A settings change
  // must not yank the board out from under a working trader, which is why the
  // other landing prefs above are frozen mount snapshots. An account change is
  // not that: those three are browser-wide values that mean the same thing to
  // everyone, while this one is PRIVATE TO A USER, so a frozen copy of it is
  // one trader's preference rendering under another's session. Reachable
  // without a remount because authStore's cross-tab listener adopts a sibling
  // tab's login in place (authStore.ts:165).
  const [landingSeededFor, setLandingSeededFor] = useState<string | null>(authUserId);
  const [defaultDashboardSport, setDefaultDashboardSport] = useState<DashboardSport>(() =>
    resolveLandingBoard(authUserId),
  );
  if (landingSeededFor !== authUserId) {
    setLandingSeededFor(authUserId);
    setDefaultDashboardSport(resolveLandingBoard(authUserId));
  }
  // ── Focused deep link (#248, extended to CFB by #539) ──────────────
  // A Position Tracker graph chip encodes the exact market it already knows
  // into this URL; the board re-derives that market from live rows and opens
  // the EXISTING panel through it. Parsing is pure and lives in
  // lib/routing/focusedDashboard; the one-shot state machine lives in
  // useFocusedDashboardHydration. Everything here is wiring.
  //
  // Note the sport filter needs no focus-specific handling: the contract's
  // `sport` key IS the dashboard's own `?sport=` param, so `selectedSport`
  // below already lands on the routed sport for a focused URL.
  //
  // The BOARD does need handling (#539): a spread or total target lives on the
  // Spread/Total tab, and which tab that is comes from `focusedBoardFor` — the
  // ticker — because the query deliberately carries no market-kind key.
  const focusQueryKey = focusedQueryString(searchParams);
  const parsedFocus = useMemo(
    () => parseFocusedDashboardQuery(new URLSearchParams(focusQueryKey)),
    [focusQueryKey],
  );
  const focusTarget = parsedFocus.kind === 'market' ? parsedFocus.target : null;
  // The trader has deliberately moved the board (sport, date, market type).
  // From then on the focus is inert for the rest of this mount, INCLUDING
  // across the identity change a sport toggle causes — `sport` is the one
  // focus-contract key the toolbar itself rewrites, so without this latch the
  // hook would see a "new" focus URL and try to hydrate the corrupted one.
  // The query is left untouched either way, so reload still reproduces the
  // original link.
  const [focusTakenOver, setFocusTakenOver] = useState(false);

  // ── Working reading view (#826) ────────────────────────────────────
  // The board's transient reading controls, resumed through the shared #824
  // baseline instead of dying with the route. Only allowlisted reading state
  // lives here — no order, arming, focus or ticket state ever enters it, and
  // the saved display defaults below keep their existing owners.
  //
  // A focused deep link reads on its OWN instance: a one-shot link's board
  // must not become the board the trader gets back when they later walk in
  // through the sidebar.
  //
  // Keyed on the URL CARRYING a focus key, not on the focus parsing cleanly:
  // an `unavailable` or malformed link is still a one-shot visit, and reading
  // it on the ordinary board group let it become the board a later sidebar
  // return landed on. And `sport` is excluded, because it is the one focus key
  // the TOOLBAR itself rewrites — the same reason `focusTakenOver` exists. Left
  // in, a sport chip changed the instance mid-visit and silently abandoned
  // everything the trader had set on the page they were still standing on.
  const readingInstance = useMemo(() => {
    const keys = new URLSearchParams(focusQueryKey);
    if (!keys.has('focus')) return 'board';
    keys.delete('sport');
    return `focus:${keys.toString()}`;
  }, [focusQueryKey]);
  const reading = useWorkingView(
    dashboardReading,
    readingInstance,
    {
      // Nothing remembered yet — the landing defaults, or the focused link's
      // own board. Explicit, not "the saved default is probably moneyline
      // already".
      sport: null,
      marketType: focusTarget ? focusedBoardFor(focusTarget.ticker) : initialMarketType,
      // A focused link's date is URL intent, so it is stored LITERALLY even
      // when it happens to be today: the link names one market on one day, and
      // a page left open past midnight must not quietly re-point it.
      dateAnchor: focusTarget ? focusTarget.date : 'today',
      dateFilter: 'today',
      // Default 'all' so FINAL rows show in the natural sort during their
      // ~5-min hydra cache lifetime. The 'active' / 'settled' refinements stay
      // available on the toolbar for users who want a filtered view.
      statusFilter: 'all',
      marketSide: focusTarget?.side ?? 'no',
      priceMode: initialPriceMode,
      // #754 — a fresh board is never searching. A focused link names one
      // market directly and has no query to carry either.
      discoveryQuery: '',
      // ...and it opens on the trader's own board, not an all-sport day.
      discoveryMode: 'manual',
    },
    { fromUrl: dashboardReadingFromUrl },
  );
  const [selectedMarketType, setSelectedMarketType] = useWorkingViewField(reading, 'marketType');
  const [dateAnchor, setDateAnchor] = useWorkingViewField(reading, 'dateAnchor');
  const [dateFilter, setDateFilter] = useWorkingViewField(reading, 'dateFilter');
  const [statusFilter, setStatusFilter] = useWorkingViewField(reading, 'statusFilter');
  const [marketSide, setMarketSide] = useWorkingViewField(reading, 'marketSide');
  const [priceMode, setPriceMode] = useWorkingViewField(reading, 'priceMode');
  // ── Overview search (#754) ─────────────────────────────────────────
  // The COMMITTED query only. The text being typed lives inside the search
  // control, so a half-typed word never narrows the board — and nothing here
  // can become an executable target: this filters which groups are on screen
  // and touches no ticket, arming or order state.
  const [discoveryQuery, setDiscoveryQuery] = useWorkingViewField(reading, 'discoveryQuery');
  const discoverySearchable = isSearchableMarketType(selectedMarketType);
  // ── Today / This week (#902's controls, mounted here) ──────────────
  // A date scope taken ACROSS sports, so it lives only where whole events
  // carry a date: the moneyline and spread/total catalogs, both of which read
  // the store's own admission day. Futures boards have no start date at all
  // (`discoveryCatalog.ts` refuses to invent one from a year label), so a mode
  // there would empty the board by construction — the coverage line says so
  // rather than leaving a trader to discover it.
  const [discoveryMode, setDiscoveryMode] = useWorkingViewField(reading, 'discoveryMode');
  const discoveryModeEligible = isModeEligibleMarketType(selectedMarketType);
  const effectiveDiscoveryMode: DiscoveryMode = discoveryModeEligible ? discoveryMode : 'manual';
  const discoveryActive = discoverySearchable
    && (isDiscoveryActive(discoveryQuery) || effectiveDiscoveryMode !== 'manual');
  // For the handlers that move SEVERAL reading fields as one gesture — a date
  // step also forces bounded mode, a focused link states four fields at once.
  const updateReading = reading.update;
  // The anchor is stored as INTENT ('today' stays relative); these two are the
  // concrete day it names right now, in the board's two spellings.
  const selectedDateIso = resolveDateAnchor(dateAnchor);
  const selectedDate = isoToMdy(selectedDateIso);
  const setSelectedDate = useCallback(
    (next: string) => setDateAnchor(toDateAnchor(mdyToIso(next))),
    [setDateAnchor],
  );

  // #826 — `?sport=` remains the authority whenever it says anything: the
  // reading view's `fromUrl` overlay writes the parameter straight over the
  // remembered value on every read, so a store write can never overrule a
  // link or a reload. What the memory answers is the case the URL is SILENT
  // about — the sidebar's bare `/dashboard`, which used to drop a working
  // trader back on their saved landing board.
  const readingSport = reading.value.sport;
  const allSports = readingSport === 'all';
  // #604/#653 — the sport key may name a dashboard-only board: the ATP
  // Challenger circuit or a CFB subdivision bucket. Everything authority-shaped
  // (getGames, display settings, shared sport APIs) runs on the underlying
  // Sport; only tabs, row filtering, and focused URLs see the DashboardSport.
  const selectedDashboardSport: DashboardSport =
    (allSports ? null : readingSport) ?? defaultDashboardSport;
  const selectedSport: Sport = underlyingSport(selectedDashboardSport);
  // Moneyline sport filter. Empty set means "All sports" (?sport=all);
  // non-empty means the single active moneyline sport. Futures and
  // spread/total are single-sport and read selectedSport — which falls back
  // to the saved default while 'all' is active.
  const selectedSports = useMemo<ReadonlySet<Sport>>(
    () => (allSports ? new Set<Sport>() : new Set([selectedSport])),
    [allSports, selectedSport],
  );
  // Consumer feed filters, Phase 2 (gated, VITE_FEED_FILTERS_SPORTS): the
  // dashboard renders one sport at a time, so it declares exactly that; the
  // 'all' chip declares every sport. The declaration is inert on Phase 1 —
  // only the class/type opt-ins ride the wire there — and the transport
  // layers hold a narrowing grace so a chip toggle back within 30 s is a
  // pure local re-filter, not a resubscribe (the Jun-12 constraint).
  // #754 widens the EXISTING declaration rather than adding a second one: a
  // committed search spans sports, so the board needs every sport's frames for
  // as long as it is searching. Passing a wider object is the whole change —
  // clearing and re-declaring would publish a transient union missing this
  // source, which is what wedged hc1 on 2026-09-05.
  // g1xl-v2#1053 Part 3 — declared below, once the slate window is known:
  // the board names the rows it draws (see `useBoardFeedNeeds`); this is
  // the declaration it falls back to.
  // g1xl-v2#1053 Part 2d — the board runs at 1 s; a focused game (a row
  // expanded into its ticket, a Spread/Total line opened) asks the browser
  // cadence. Declared here so the state exists before the declaration.
  const [expandedGameKey, setExpandedGameKey] = useState<string | null>(null);
  const [spreadTotalGame, setSpreadTotalGame] = useState<GameRow | null>(null);
  const boardFocused = expandedGameKey != null || spreadTotalGame != null;
  const broadFeedNeeds = useMemo<FeedNeeds>(
    () => ({
      ...(allSports || discoveryActive ? { sports: 'all' as const } : { sports: [...selectedSports] }),
      cadenceMs: boardFocused ? BROWSER_CADENCE_MS : BOARD_IDLE_CADENCE_MS,
    }),
    [allSports, discoveryActive, selectedSports, boardFocused],
  );
  // Same selection in dashboard keys, so a CFB bucket or the ATP Challenger
  // circuit survives. Lives up here beside its underlying-Sport twin because
  // the weekly-slate gate below needs it before the board reads any rows.
  const selectedDashboardSports = useMemo<ReadonlySet<DashboardSport>>(
    () => (allSports ? new Set<DashboardSport>() : new Set<DashboardSport>([selectedDashboardSport])),
    [allSports, selectedDashboardSport],
  );
  // #212 — the #192 session-local hide toggle became the persisted per-sport
  // suppression list. Read reactively (unlike the mount-time landing prefs)
  // because the toolbar chip must flip the board live; default [] preserves
  // the GBuff show-everything experience until the user opts in.
  const hiddenEmptySports = useDisplaySettings((s) => s.hiddenEmptySports);
  const setHiddenEmptySports = useDisplaySettings((s) => s.setHiddenEmptySports);
  // #509 — same reactive-read reasoning as hiddenEmptySports above: the toolbar
  // chip must flip the board live, and the default `false` preserves today's
  // production experience until the user opts in.
  const kalshiMarketsOnly = useDisplaySettings((s) => s.kalshiMarketsOnly);
  const setKalshiMarketsOnly = useDisplaySettings((s) => s.setKalshiMarketsOnly);
  // #654 — same reactive read, same reason.
  const cfbSavedOnly = useDisplaySettings((s) => s.cfbSavedOnly);
  const setCfbSavedOnly = useDisplaySettings((s) => s.setCfbSavedOnly);
  // ── Weekly slate (#712) ─────────────────────────────────────────────
  // The persisted Day/Week choice, read reactively for the same reason as
  // hiddenEmptySports above: the toolbar chip must flip the board live.
  //
  // `effectiveSlateRange` is what the board actually runs on. A stored `week`
  // goes INERT — not erased — on any board without a weekly slate, so adding
  // MLB to the selection returns that board to its daily view while the
  // trader's football choice survives for their next football board. That is
  // the guardrail against silently widening an unrelated sport.
  //
  // `selectedDate` stays the anchor in both modes: the window is derived from
  // it rather than replacing it, so a focused deep link still lands on its
  // market's own date, and Week → Day → Week returns where it started instead
  // of snapping to a window boundary.
  const slateRange = useDisplaySettings((s) => s.slateRange);
  // #1398 — the board's NBA injury entries follow the reader's injury display
  // preference (off by default): no injury line on the board unless asked for.
  const showInjuryPanels = useDisplaySettings((s) => s.showInjuryPanels);
  const setSlateRange = useDisplaySettings((s) => s.setSlateRange);
  const slateRangeEligible = useMemo(
    () =>
      weeklySlateEligible({
        selectedSports: selectedDashboardSports,
        marketType: selectedMarketType,
        allSports,
      }),
    [selectedDashboardSports, selectedMarketType, allSports],
  );
  const effectiveSlateRange: SlateRange = slateRangeEligible ? slateRange : 'day';
  const slateDates = useMemo(
    () => slateWindow(selectedDateIso, effectiveSlateRange),
    [selectedDateIso, effectiveSlateRange],
  );
  // g1xl-v2#1053 Part 3 — the board must not take lines it does not draw.
  // On a single binary-game sport's moneyline or Spread/Total reading the
  // declaration names the drawn rows (moneyline pair + the nearest-three
  // spread/total window per game) from a light index on the owning edge,
  // instead of the sport's whole alternate ladder. All-sports, discovery,
  // soccer's 3-way board, golf, series and futures keep the broad
  // declaration: their rows are not a per-game line pick.
  const boardReading: BoardReading | null =
    selectedMarketType === 'moneyline' ? 'moneyline'
      : selectedMarketType === 'spread-total' ? 'spread-total'
        : null;
  const boardScoped = !allSports && !discoveryActive && boardReading != null
    && !MULTI_OUTCOME_SPORTS.has(selectedSport) && !STANDALONE_SPORTS.has(selectedSport);
  useBoardFeedNeeds(VALUE_DASHBOARD_FEED_SOURCE, {
    scoped: boardScoped,
    sport: selectedSport,
    reading: boardReading ?? 'moneyline',
    dayWindow: dateFilter === 'today' ? slateDates : null,
    broad: broadFeedNeeds,
  });
  // odds-gamestate-feed-filters D6 (the spec's D2 table): the board's OP
  // facet, for the sports it shows. The moneyline board (GameTable, the
  // soccer 3-way board, Saved, discovery results) draws full-game
  // moneyline; the Spread/Total tab draws full-game spread + total at its
  // chips (and the ±3 lines of an expanded row). Series / futures draw no
  // OP. Off with the OP column. Inert unless VITE_FEED_FILTERS_ODDS is on.
  const boardShowOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const boardOddsTypes = useMemo<string[] | null>(() => {
    const types = new Set<string>();
    if (boardReading === 'moneyline' || discoveryActive) types.add('moneyline');
    if (boardReading === 'spread-total') { types.add('spread'); types.add('total'); }
    return types.size > 0 ? [...types] : null;
  }, [boardReading, discoveryActive]);
  useOddsFeedNeeds('value-dashboard:odds', boardShowOpOdds && boardOddsTypes
    ? { board: { sports: broadFeedNeeds.sports ?? 'all', marketTypes: boardOddsTypes, periods: ['full'] } }
    : null);
  // Dan, 2026-10-02: an empty board says "Loading" until the feed has
  // actually answered for this selection, and "No games" only once that is
  // known (lib/feedFilter/feedSettling). Keyed by what the board is FOR —
  // never by the focus cadence, which changes no rows.
  const feedSettlingKey = [
    allSports ? 'all' : selectedDashboardSport, selectedMarketType, slateDates.startIso, slateDates.endIso,
    dateFilter, statusFilter, discoveryActive ? 'discovery' : 'board',
  ].join('|');
  const boardFeed = useFeedSettlingState(feedSettlingKey, allSports ? null : selectedSport, 'value-dashboard');
  const boardLoading = boardFeed.kind === 'settling';
  // What the toolbar's date pill reads. Day mode keeps the plain MM/DD/YYYY
  // it has always shown; a weekly slate names both ends of its window so the
  // span is exact and inclusive on its face. null falls the pill back to the
  // anchor date — a window we can't name is better shown as the date the user
  // actually chose than as broken text.
  const slateRangeLabel = effectiveSlateRange === 'week' ? formatSlateRange(slateDates) : null;
  // The same window in the empty states' voice: "on 09/07" vs "in Sep 7 – Sep 13".
  const requestedSlateCopy = slateScopeCopy(slateDates, effectiveSlateRange);
  const selectedSportsKey = useMemo(
    () => sortSports(selectedSports).join('|'),
    [selectedSports],
  );
  const showAllSportsChip = selectedMarketType === 'moneyline';
  const isGolfMatchBoard = selectedMarketType === 'moneyline'
    && selectedSports.size === 1 && selectedSports.has('golf');
  const golfMatchesAvailable = getGolfMatches().length > 0;
  const selectedMoneylineSports = useMemo<Sport[]>(() => {
    const sports =
      selectedSports.size === 0
        ? SPORT_TABS.map((tab) => tab.id)
        : sortSports(selectedSports);
    return sports.filter((sport) => !MULTI_OUTCOME_SPORTS.has(sport));
  }, [selectedSports, selectedSportsKey]);
  const singleSelectedMultiOutcomeSport = useMemo<Sport | null>(() => {
    if (selectedMarketType !== 'moneyline' || selectedSports.size !== 1) return null;
    const [sport] = Array.from(selectedSports);
    return MULTI_OUTCOME_SPORTS.has(sport) ? sport : null;
  }, [selectedMarketType, selectedSports, selectedSportsKey]);
  // Which sports a committed search has to read, per family. Inert — the
  // board's own selection — whenever nothing is committed, so the ordinary
  // view costs exactly what it costs today.
  const discoveryPlan = useMemo(
    () => discoveryCatalogPlan({
      active: discoveryActive,
      marketType: selectedMarketType,
      boardMoneylineSports: selectedMoneylineSports,
      boardMultiSport: singleSelectedMultiOutcomeSport,
      boardSport: selectedSport,
    }),
    [discoveryActive, selectedMarketType, selectedMoneylineSports,
      singleSelectedMultiOutcomeSport, selectedSport],
  );

  // Re-apply a focused link's board state whenever the focus IDENTITY changes
  // while this component stays mounted (Back/Forward between two focused
  // links, or a second chip click). Deliberately silent once the trader has
  // taken the board over.
  //
  // A LAYOUT effect, not a passive one. On a Back onto a focus entry the
  // trader had edited, the entry's own record resumes first; a passive write
  // then overwrote it AFTER the browser had already painted it, so the board
  // visibly flipped a frame later (measured: the pre-write value survives a
  // paint boundary). The link is explicit URL intent and still wins — it just
  // has to win before the paint, the way the shared contract overlays every
  // other URL-owned field. Note this is a deliberate divergence from that
  // contract's "on POP, only that history entry resumes" default: #248's
  // deep-link precedence is fenced off by this ticket, and its shipped
  // behaviour is that the link re-asserts its own market.
  //
  // One write, not four: the four fields describe ONE market, so a focus must
  // never be half-applied — and #826's store records a whole slice per edit.
  useLayoutEffect(() => {
    if (focusTakenOver || parsedFocus.kind !== 'market') return;
    const { ticker, date, side } = parsedFocus.target;
    updateReading((previous) => ({
      ...previous,
      marketType: focusedBoardFor(ticker),
      // Literal, for the same reason as the defaults above.
      dateAnchor: date,
      dateFilter: 'today',
      marketSide: side,
      // #754/#902 — a link that names ONE market is the most explicit scope
      // statement the board can receive, so it ends any discovery the same way
      // a sport chip or a date gesture does. It bites on RE-opening a focus
      // link the trader had already put into Today: the reading record is
      // addressed by the whole URL, so that mode resumes, while this hydration
      // — inert only once the board has been taken over — runs again on the
      // new mount. Left standing, the all-sport scope re-filters the very
      // board the link exists to reveal, and can hide the market it names.
      discoveryQuery: '',
      discoveryMode: 'manual',
    }));
  }, [parsedFocus, focusTakenOver, updateReading]);

  // Heal stale state on any toolbar interaction. Skips on first render
  // (the connect-time snapshot covers initial load) and is a no-op
  // unless ~90% of tickers have gone silent in the last
  // CHIP_RESNAP_STALE_MS — guards against the failure mode where one
  // chatty ticker keeps the connection looking alive while everything
  // else is throttled or upstream-dead. Pure-display toggles like
  // marketSide / priceMode don't need new data, but firing the check on
  // them is essentially free and catches "I sat idle, came back, clicked
  // anything" stale paths.
  const firstToolbarRender = useRef(true);
  useEffect(() => {
    if (firstToolbarRender.current) {
      firstToolbarRender.current = false;
      return;
    }
    requestResnapshotIfStale(CHIP_RESNAP_STALE_MS);
  }, [
    selectedSport,
    selectedDashboardSport,
    selectedSportsKey,
    selectedMarketType,
    selectedDate,
    dateFilter,
    statusFilter,
    marketSide,
    priceMode,
    requestResnapshotIfStale,
  ]);

  // #826 — the reading filters that can legitimately empty a board, named for
  // the empty state. Only NARROWING ones: 'All' dates widens, and a board on
  // today with every status is simply a board, not a filtered view.
  const readingFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (statusFilter !== 'all') parts.push(statusFilter === 'active' ? 'Active' : 'Settled');
    // The SAME predicate the Today chip lights on, deliberately: a weekly slate
    // anchored on tomorrow still contains today, so naming its date would
    // claim a filter that clears nothing while the chip says you are on today.
    if (selectedMarketType !== 'series' && dateFilter === 'today' && !slateWindowContains(slateDates, todayIso())) {
      parts.push(slateRangeLabel ?? selectedDate);
    }
    return parts.length > 0 ? `Filters active: ${parts.join(' · ')}` : null;
  }, [selectedMarketType, statusFilter, dateFilter, slateDates, slateRangeLabel, selectedDate]);

  // ── Position sidebar state ─────────────────────────────────────────
  const [showPositionSidebar, setShowPositionSidebar] = useState(false);

  const selectedMoneylineSportSet = useMemo(
    () => new Set(selectedMoneylineSports),
    [selectedMoneylineSports],
  );
  const selectedMoneylineLabelSports = useMemo(
    () => (selectedSports.size === 0 ? selectedMoneylineSports : sortSports(selectedSports)),
    [selectedMoneylineSports, selectedSports, selectedSportsKey],
  );
  // A selected CFB bucket qualifies the board's own name ("CFB · FBS"), so the
  // table header states which slice is on screen on every board, not just
  // moneyline.
  // Null on Futures for the same reason the chips hide there (see the toolbar):
  // a bucket has no meaning on a championship market, so the board must not
  // name one in its title or empty state either.
  const selectedCfbBucket =
    allSports || selectedMarketType === 'series' ? null : cfbBucketOf(selectedDashboardSport);
  // #654 — a CFB MATCHUP board is on screen, as the whole slate or as one
  // bucket. The single owner of that question: it gates the toolbar's CFB
  // controls (both #653's buckets and #654's Saved-only chip) AND the lens
  // filters below, and those must never disagree. Same two exclusions the
  // bucket carries — "All sports" has no sub-board, and a championship future
  // is one market listing many teams rather than a matchup.
  const cfbBoardSelected =
    !allSports &&
    underlyingSport(selectedDashboardSport) === 'cfb' &&
    selectedMarketType !== 'series';
  const selectedCfbBucketLabel = selectedCfbBucket ? CFB_BUCKET_LABEL[selectedCfbBucket] : null;
  const baseSportFilterLabel =
    selectedMarketType === 'moneyline'
      ? selectedDashboardSport === ATP_CHALLENGER_DASHBOARD_SPORT && !allSports
        ? 'ATP Challenger'
        : sportSelectionLabel(selectedMoneylineLabelSports, selectedSports.size === 0)
      : SPORT_LABEL[selectedSport] ?? selectedSport.toUpperCase();
  const sportFilterLabel = selectedCfbBucketLabel
    ? `${baseSportFilterLabel} · ${selectedCfbBucketLabel}`
    : baseSportFilterLabel;
  const tableTitleLabel =
    selectedMarketType === 'moneyline'
      ? `${sportFilterLabel} Moneylines`
      : undefined;

  // ── Data from Hydra via Zustand ─────────────────────────────────────
  // boardRevKey moves when a sport on the board publishes → fresh derivation from mutable maps.
  // Series rows merge cross-venue via canonicalSeriesKey inside marketStore;
  // no client-side merge needed.
  const boardSports = useMemo<readonly Sport[]>(() => {
    const set = new Set<Sport>(selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport]);
    return [...set].sort();
  }, [selectedMarketType, selectedMoneylineSports, selectedSport]);
  /** A scalar over the board's sports' revisions: unchanged by any other sport's frame. */
  const sportRevKey = useMarketStore((s) => boardSports.map((sp) => s.revBySport[sp] ?? 0).join('|'));
  // Quiet books still age and scheduled games still cross time boundaries.
  // Reuse the row display clock; payload changes remain immediate and unrelated
  // sport publications still do not trigger a selected-board derivation.
  const boardNow = useSharedNow(1000);
  const boardRevKey = `${sportRevKey}:${Math.floor(boardNow / 1000)}`;
  const liveGamesForFilter = useMemo(() => {
    void boardRevKey; // explicit dependency
    if (selectedMarketType === 'series') return [];
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    const rows = sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter,
      // The bounded view's inclusive window (#712). Day mode passes the same
      // date as both ends, so the store's filter is unchanged for it.
      selectedDateIso: slateDates.startIso,
      rangeEndIso: slateDates.endIso,
    }));
    // #604 — split the ATP circuits on a single-sport selection. All-sports
    // deliberately stays unfiltered: the aggregate board keeps both circuits.
    return allSports
      ? rows
      : rows.filter((row) => dashboardSportMatches(row, selectedDashboardSport));
  }, [
    getGames,
    selectedSport,
    selectedDashboardSport,
    allSports,
    selectedMoneylineSports,
    selectedMarketType,
    boardRevKey,
    statusFilter,
    dateFilter,
    slateDates,
  ]);

  const upcomingFallbackSlate = useMemo(() => {
    void boardRevKey;
    // #248 — a focus query means exact-date mode, always. A stale link must
    // not appear to land on a nearby event, so the fallback is off for every
    // focus kind, valid or not.
    if (parsedFocus.kind !== 'none') return null;
    if (selectedMarketType === 'series' || dateFilter !== 'today' || liveGamesForFilter.length > 0) {
      return null;
    }
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    // All upcoming games for the selected sport(s), excluding final/series/archived.
    // Eligibility is settled HERE, before a date is chosen — see pickUpcomingSlate.
    const eligible = sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter: 'all',
      selectedDateIso: slateDates.startIso,
    })).filter((game) => {
      if (game.series || game.archived || game.gameState === 'final') return false;
      // #604 — the upcoming fallback obeys the same circuit split as the
      // live rows; all-sports keeps both circuits.
      return allSports || dashboardSportMatches(game, selectedDashboardSport);
    });
    return pickUpcomingSlate({
      rows: eligible,
      fromIso: slateDates.startIso,
      range: effectiveSlateRange,
      dateOf: (game) => game.date,
      sortMs: gameSortMs,
      tieBreak: (game) => game.awayCode,
    });
  }, [
    getGames,
    selectedSport,
    selectedDashboardSport,
    allSports,
    selectedMoneylineSports,
    selectedMarketType,
    boardRevKey,
    statusFilter,
    dateFilter,
    slateDates,
    effectiveSlateRange,
    liveGamesForFilter.length,
    parsedFocus.kind,
  ]);

  const usingUpcomingFallback = dateFilter === 'today'
    && liveGamesForFilter.length === 0
    && upcomingFallbackSlate != null;
  const liveGames = usingUpcomingFallback && upcomingFallbackSlate
    ? upcomingFallbackSlate.rows
    : liveGamesForFilter;
  // Closed games sourced from the analytics endpoint (Postgres-backed,
  // 30-day lookback). Skipped on the series tab and on non-moneyline
  // market types — analytics endpoint returns moneyline-shaped summaries
  // per game; spread/total surfaces aren't a fit for the skinny-row
  // projection. `statusFilter='active'` also short-circuits since
  // archived rows are by definition non-active.
  const archivedDates = useMemo(
    () => slateWindowDates(slateDates),
    [slateDates],
  );
  const { archived: archivedRaw, loading: archivedLoading } = useArchivedGames(
    selectedMarketType === 'moneyline' && statusFilter !== 'active'
      ? archivedDates
      : null,
  );

  const gamesRaw = useMemo<GameRow[]>(() => {
    // Any FINAL row from the live store gets collapsed into the skinny
    // archived shape so finished games render the same one-liner
    // regardless of whether they came from `_markets` (still in
    // session-cached hydra state) or the analytics fetch. WNBA was the
    // visible offender — its games sit in `_markets` longer than other
    // sports' do post-close, so they kept rendering as full live rows.
    const liveTransformed = liveGames.map((g) =>
      g.gameState === 'final' && !g.archived
        ? {
            ...g,
            archived: {
              // A FINAL row's volume is only shown when it is an observed
              // ticker scalar this session (DNL-R6-P1); otherwise the archived
              // one-liner dashes it like any other unobserved figure.
              kalshiVolume: observedVolume(g.kalshi?.away) ?? 0,
              polyVolume: observedVolume(g.polymarket?.away) ?? 0,
            },
          }
        : g,
    );
    if (archivedRaw.length === 0) return liveTransformed;
    // Dedupe: drop any analytics row whose canonical gameKey already
    // appears in the live set (live data wins). Live rows use the
    // marketStore's own key shape; normalize through toHydraGameKey so
    // the comparison survives the sport-prefix vs no-prefix variants.
    const liveCanonicalKeys = new Set<string>();
    for (const g of liveTransformed) {
      const k = toHydraGameKey(g.key);
      if (k) liveCanonicalKeys.add(k);
    }
    const archivedRows: GameRow[] = [];
    for (const a of archivedRaw) {
      const sport = a.sport as Sport;
      if (selectedMarketType === 'moneyline') {
        if (!selectedMoneylineSportSet.has(sport)) continue;
      } else if (sport !== selectedSport) continue;
      if (liveCanonicalKeys.has(a.gameKey)) continue;
      archivedRows.push(archivedToGameRow(a));
    }
    if (archivedRows.length === 0) return liveTransformed;
    // Sort archived rows by date desc so newest closed games sit
    // immediately under the live block.
    archivedRows.sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
    return [...liveTransformed, ...archivedRows];
  }, [
    liveGames,
    archivedRaw,
    selectedSport,
    selectedMarketType,
    selectedMoneylineSportSet,
  ]);

  /**
   * #730 — `getGames` rebuilds every row on every publication, so `gamesRaw`
   * is 15 fresh objects a tick whether or not anything on this sport moved.
   * The identity store compares each against the row it replaced BY VALUE and
   * hands back the older reference only when the two are interchangeable —
   * nothing withheld, delayed, or served stale; anything it cannot prove
   * equal counts as changed. The ARRAY keeps its identity too when no row
   * moved, so every memo keyed on `games` below stays put. One store per
   * list: the Saved board gets its own. See `stableGameRows.ts`.
   */
  const [gameRowIdentity] = useState(() => createGameRowIdentityStore());
  const games = useMemo(
    () => gameRowIdentity.stabilize(gamesRaw),
    [gameRowIdentity, gamesRaw],
  );

  // ── Search sources (#754) ──────────────────────────────────────────
  // Separate reads rather than a widening of the board's own memos above. A
  // committed search is a temporary state; the ordinary board must keep
  // deriving from exactly the sports it renders, and must not start tracking
  // every sport's revision because the search feature exists. Each memo below
  // returns immediately when nothing is committed.
  //
  // Placed after the upcoming-day fallback deliberately: search has to look at
  // the window the trader is LOOKING AT. A board that fell forward to the next
  // day with games, searched over the empty day it asked for, answers "no
  // matches" about games that are on screen.
  const discoverySportRevKey = useMarketStore((s) => (discoveryActive
    ? DISCOVERY_SPORTS.map((sp) => s.revBySport[sp] ?? 0).join('|') : ''));
  const discoveryRevKey = `${discoverySportRevKey}:${Math.floor(boardNow / 1000)}`;
  /**
   * #902 — the reader's own zone, resolved once. The selector is handed the
   * same string, so the day a mode names and the day its rows were read over
   * are the same day by construction rather than by two lookups agreeing.
   */
  const discoveryTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone, [],
  );
  const readerDay = calendarDateInZone(boardNow, discoveryTimeZone);
  /**
   * The exact inclusive window Today / This week name, or null in Manual.
   *
   * Memoised on the reader's calendar DAY rather than on `boardNow`: the
   * clock ticks every second and this answer changes once a day, so keying it
   * on the tick would hand every memo below a new object per second.
   * `discoveryRangeForDay` is `discoveryRange` with the day already resolved,
   * so the two cannot drift: the selector asks the clock, this asks the day
   * that clock falls on, and both run the same calendar arithmetic.
   */
  const discoveryModeRange = useMemo(
    () => discoveryRangeForDay(effectiveDiscoveryMode, readerDay, DISCOVERY_WEEK, null),
    [effectiveDiscoveryMode, readerDay],
  );
  /**
   * The window the search and the mode actually READ over.
   *
   * A mode states its own dates and is never subject to the upcoming-day
   * fall-forward: "Today" that quietly showed tomorrow because today is empty
   * is the exact thing #902 forbids. In Manual the board keeps the behaviour
   * it has — search looks at the window the trader is LOOKING AT.
   */
  const discoveryWindow: SlateWindow = discoveryModeRange
    ?? (usingUpcomingFallback && upcomingFallbackSlate
      ? upcomingFallbackSlate.window
      : slateDates);
  // D-020 LT2-005, asked once for the whole board: the spread/total execution
  // rule the SpreadsTotals board applies to its own rows, which discovery must
  // apply to the events it puts in the catalog — see `discoveryLineEvents`.
  const hidePolyOnlyLines = useHidePolyOnlyLines();
  const discoveryMoneylinesRaw = useMemo(() => {
    if (!discoveryActive) return [];
    void discoveryRevKey;
    // The board's own date/status window travels with the search: a committed
    // query widens the SPORT scope and nothing else. A MODE states the window
    // itself, so it reads bounded whatever the date chip underneath says —
    // that chip is the saved manual scope, waiting, not the scope in force.
    return discoveryPlan.moneylineSports.flatMap((sport) => getGames(sport, 'moneyline', {
      statusFilter,
      dateFilter: discoveryModeRange ? 'today' : dateFilter,
      selectedDateIso: discoveryWindow.startIso,
      rangeEndIso: discoveryWindow.endIso,
    }));
  }, [discoveryActive, getGames, discoveryPlan, statusFilter, dateFilter, discoveryModeRange,
    discoveryWindow, discoveryRevKey]);
  /**
   * #754 — the same identity treatment the board's own rows get.
   *
   * `getGames` rebuilds every row on every publication AND drops its whole
   * cache on each clock second, so without this every result row is a fresh
   * object once a second and `memo(GameRows)` can never bail — on a wide
   * search with Show all that is the whole eligible set re-rendering on a
   * timer. Its own store, like the Saved board's: a row can be on both lists.
   */
  const [discoveryRowIdentity] = useState(() => createGameRowIdentityStore());
  const discoveryMoneylines = useMemo(
    () => discoveryRowIdentity.stabilize(discoveryMoneylinesRaw),
    [discoveryRowIdentity, discoveryMoneylinesRaw],
  );
  const discoveryMultiEvents = useMemo(() => {
    if (!discoveryActive) return [];
    void discoveryRevKey;
    // This store reader supports one day only. The discovery selector applies
    // the full reading window, including a football board's seven-day scope.
    return discoveryPlan.multiSports.flatMap((sport) => getMultiOutcomeEvents(sport, {
      statusFilter, dateFilter: 'all',
    }));
  }, [discoveryActive, getMultiOutcomeEvents, discoveryPlan, statusFilter,
    discoveryRevKey]);
  const discoveryFutureBoards = useMemo<FuturesBoard[]>(() => {
    if (!discoveryActive) return [];
    void discoveryRevKey;
    return discoveryPlan.futuresSports.flatMap((sport) => [
      ...getSeriesEvents(sport).map((event) => ({
        ...event,
        outcomes: event.outcomes.filter(
          (outcome) => futuresStatusMatches(outcome.kalshi?.status, statusFilter)),
      })).filter((event) => event.outcomes.length > 0),
      ...filterFutureBoards(getFutureBoards(sport), statusFilter),
    ]);
  }, [discoveryActive, getSeriesEvents, getFutureBoards, discoveryPlan, statusFilter, discoveryRevKey]);
  /**
   * #754 — whole spread/total events, across sports, for the same window.
   *
   * Reads exactly like the moneyline half above, and costs what that costs for
   * the same reason: `getGameEvents` now holds a version-stamped result cache
   * (`marketStore._getGameEventsCache`), so one call per sport per derivation
   * is one REBUILD per sport per store revision, not one full ticker re-parse
   * per call. Without that cache this read is the unbounded fan-out the
   * acceptance criteria forbid, which is why it was deferred until it existed.
   *
   * No period argument: the catalog is full-game lines, and `discoveryCoverage`
   * says so rather than leaving a trader to infer it from a 1H search that
   * finds nothing.
   */
  const discoveryLineEvents = useMemo<readonly GameEventRow[]>(() => {
    if (!discoveryActive) return [];
    void discoveryRevKey;
    const fetched = discoveryPlan.linesSports.flatMap((sport) => getGameEvents(sport, {
      statusFilter,
      dateFilter: discoveryModeRange ? 'today' : dateFilter,
      selectedDateIso: discoveryWindow.startIso,
      rangeEndIso: discoveryWindow.endIso,
    }));
    // The execution rule, applied BEFORE the catalog counts these events
    // (D-020 LT2-005, and `useHidePolyOnlyLines`' own note). An event whose
    // every line is hidden from the execution view is not a result: leaving it
    // in would have the scope line count a row the board then declines to
    // draw, which is the "Showing 20 of 25 over a shorter list" the search
    // contract already refuses for the Kalshi-only chip.
    return executableEvents(fetched, hidePolyOnlyLines);
  }, [discoveryActive, getGameEvents, discoveryPlan, statusFilter, dateFilter,
    discoveryModeRange, discoveryWindow, discoveryRevKey, hidePolyOnlyLines]);

  const [savedRowIdentity] = useState(() => createGameRowIdentityStore());
  const savedRows = useMemo(
    () => savedRowIdentity.stabilize(games.filter((g) => isRowSaved(g.key, savedIndex, authUserId))),
    [savedRowIdentity, games, savedIndex, authUserId],
  );

  // Saved games with no row on the board at all — hydra has pruned them and
  // the analytics window (~7 days) no longer covers them, or they're simply
  // on another date. Rendered from the persisted metadata alone so they stay
  // identifiable and removable; deliberately NOT date-filtered, because a
  // saved game the user can only reach by guessing the right date is not
  // removable in any practical sense. Sport filter still applies, so this
  // matches the listed rows' behavior.
  const savedUnlisted = useMemo(() => {
    // Until the feed is up, `games` is empty for reasons that have nothing to
    // do with the saved games — declaring them all "not currently listed" on
    // every page load would be a lie that flickers. Absence is only evidence
    // once there's a connection to be absent from.
    if (!connected) return [];
    const onBoard = new Set<string>();
    for (const g of games) {
      const id = gameMarkId(g.key);
      if (id) onBoard.add(id);
      const collapsed = collapsedGameKey(g.key);
      if (collapsed) onBoard.add(collapsed);
    }
    return savedEntries.filter((e) => {
      if (onBoard.has(e.id)) return false;
      const collapsed = collapsedGameKey(e.id);
      if (collapsed && onBoard.has(collapsed)) return false;
      const sport = e.sport as Sport;
      return selectedMarketType === 'moneyline'
        ? selectedMoneylineSportSet.has(sport)
        : sport === selectedSport;
    });
  }, [
    connected,
    games,
    savedEntries,
    selectedMarketType,
    selectedMoneylineSportSet,
    selectedSport,
  ]);

  const seriesEvents = useMemo<FuturesBoard[]>(() => {
    void boardRevKey;
    if (selectedMarketType !== 'series') return [];
    const kalshi = getSeriesEvents(selectedSport).map((event) => ({ ...event,
      outcomes: event.outcomes.filter((outcome) => futuresStatusMatches(outcome.kalshi?.status, statusFilter)),
    })).filter((event) => event.outcomes.length > 0);
    const poly = filterFutureBoards(getFutureBoards(selectedSport), statusFilter);
    return [...kalshi, ...poly];
  }, [getSeriesEvents, getFutureBoards, selectedSport, selectedMarketType, statusFilter, boardRevKey]);

  // Multi-outcome (3-way) sports — soccer Home/Away/Draw. The toolbar's
  // date/status filters apply exactly like the binary table's, including
  // the 7-day upcoming fallback when the selected day has no games.
  const multiOutcomeSport = singleSelectedMultiOutcomeSport;
  const isMultiOutcomeSport = multiOutcomeSport != null;
  const multiEventsForFilter = useMemo(() => {
    void boardRevKey;
    if (!multiOutcomeSport) return [];
    return getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter, selectedDateIso });
  }, [getMultiOutcomeEvents, multiOutcomeSport, boardRevKey, statusFilter, dateFilter, selectedDateIso]);
  const upcomingFallbackMultiEvents = useMemo(() => {
    void boardRevKey;
    if (!multiOutcomeSport || dateFilter !== 'today' || multiEventsForFilter.length > 0) {
      return [];
    }
    const dayStartMs = startOfIsoDayMs(selectedDateIso);
    if (!Number.isFinite(dayStartMs)) return [];
    const eventMs = (e: MultiOutcomeEvent) =>
      e.startTimeMs ?? (e.date ? startOfIsoDayMs(e.date) : Number.POSITIVE_INFINITY);
    const upcoming = getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter: 'all' })
      .filter((e) => e.gameState !== 'final' && eventMs(e) >= dayStartMs);
    if (upcoming.length === 0) return [];
    // Same rule as the binary fallback, and the same DEFINITION of a date:
    // `isSlateDate` is what `pickUpcomingSlate` refuses rows by (#765), so the
    // two copies of this rule can't disagree about which events can be placed
    // on a day. The comparison below is a STRING comparison, so a truthy-but-
    // unreal date wins it whenever it sorts first — `2026-09-3` out-sorts
    // `2026-09-30` by running out of characters, and `2026-02-30` matches the
    // pattern while naming no day at all. Either used to take the slate and
    // put a day on screen that the calendar does not have.
    //
    // Not every malformed date gets this far: `2026-09-00` dies at `eventMs`
    // above, where `startOfIsoDayMs` returns NaN and `NaN >= dayStartMs` is
    // false. That gate is not a validator, though — it admits every shape it
    // can coerce to an instant — so it is not what makes this safe. #793.
    //
    // `eventMs` above is deliberately left alone. It decides only what enters
    // the candidate pool, and an event that can no longer BECOME `nextDate`
    // cannot match the `=== nextDate` filter below either — so admitting it
    // costs nothing, and touching the sort would change row order, which this
    // change is expressly not for.
    let nextDate: string | null = null;
    for (const e of upcoming) {
      if (e.date != null && isSlateDate(e.date) && (nextDate === null || e.date < nextDate)) nextDate = e.date;
    }
    if (nextDate === null) return [];
    return upcoming.filter((e) => e.date === nextDate);
  }, [
    getMultiOutcomeEvents,
    multiOutcomeSport,
    boardRevKey,
    statusFilter,
    dateFilter,
    selectedDateIso,
    multiEventsForFilter.length,
  ]);
  const usingMultiUpcomingFallback =
    dateFilter === 'today' &&
    multiEventsForFilter.length === 0 &&
    upcomingFallbackMultiEvents.length > 0;

  // Knockout "To Advance" swap: a knockout game's headline moneyline is the
  // 2-way "who advances" market, not the regulation 1X2. getAdvanceEvents keys
  // its events identically to the 1X2, so swap them in place — group-stage games
  // (no advance market) keep their 3-way unchanged. Everything downstream
  // (rendering, trade clicks, orderbook/POS needs, the detail panel) reads
  // `multiEvents`, so the swap covers them all. See HYDRA_API.md "Soccer To Advance".
  // The swap is gated per fixture by advanceReplacesResult over the evidence
  // the store carried on the event: a bookless or Poly-only advance frame
  // never displaces a Kalshi-quoted 1X2 (2026-09-10).
  const advanceEventsByKey = useMemo(() => {
    void boardRevKey;
    const m = new Map<string, MultiOutcomeEvent>();
    if (!multiOutcomeSport) return m;
    for (const e of getAdvanceEvents(multiOutcomeSport)) m.set(e.key, e);
    return m;
  }, [getAdvanceEvents, multiOutcomeSport, boardRevKey]);
  const baseMultiEvents = usingMultiUpcomingFallback ? upcomingFallbackMultiEvents : multiEventsForFilter;
  const multiEvents = useMemo(
    () =>
      advanceEventsByKey.size === 0
        ? baseMultiEvents
        : baseMultiEvents.map((e) => {
            const adv = advanceEventsByKey.get(e.key);
            return adv && advanceReplacesResult(adv) ? adv : e;
          }),
    [baseMultiEvents, advanceEventsByKey],
  );
  // The single date the multi fallback landed on — drives the empty-state copy.
  const multiUpcomingFallbackDate = usingMultiUpcomingFallback
    ? upcomingFallbackMultiEvents[0]?.date ?? null
    : null;

  // Sports with ≥1 live market, per hydra's activeSports meta frame.
  // Toolbar uses this to auto-hide chips for sports with no activity today.
  // Always include the currently-selected sport so the active chip doesn't
  // vanish mid-interaction even before the first frame for that sport
  // arrives (and so users can navigate back to it).
  const activeSports = useMemo(() => {
    void activeSportsKey;
    const set = getActiveSports();
    if (golfMatchesAvailable) set.add('golf');
    if (selectedMarketType === 'series') {
      for (const sport of getFutureSports()) set.add(sport);
    }
    for (const sport of MULTI_OUTCOME_SPORTS) {
      if (getMultiOutcomeEvents(sport).length > 0) set.add(sport);
    }
    set.add(selectedSport);
    for (const sport of selectedSports) set.add(sport);
    // Drop edge-only sports (NFL) that this deploy can't actually serve. Prod
    // hydra's activeSports meta frame advertises NFL, but on a firehose-only
    // lane (no edge configured) no NFL game data arrives — so the chip would be
    // a dead end. Keep the explicitly-selected sport so a deep-link still works.
    for (const sport of [...set]) {
      if (sport !== selectedSport && !sportIsServable(sport)) set.delete(sport);
    }
    return set;
  }, [getActiveSports, getMultiOutcomeEvents, getFutureSports, selectedMarketType, activeSportsKey, selectedSport, selectedSports, golfMatchesAvailable]);

  // #604 — Challenger chip discovery. Hydra's activeSports meta frame only
  // knows 'atp', so the chip gates on an actual live exact-series row (any
  // date). The currently-selected key stays visible mid-interaction,
  // mirroring `set.add(selectedSport)` above.
  const challengerActive = useMemo(() => {
    void atpRev;
    return getGames('atp', 'moneyline', {
      statusFilter: 'active',
      dateFilter: 'all',
      selectedDateIso,
    }).some(isAtpChallengerRow);
  }, [getGames, atpRev, selectedDateIso]);
  const activeDashboardSports = useMemo<Set<DashboardSport>>(() => {
    const set: Set<DashboardSport> = new Set(activeSports);
    if (challengerActive || selectedDashboardSport === ATP_CHALLENGER_DASHBOARD_SPORT) {
      set.add(ATP_CHALLENGER_DASHBOARD_SPORT);
    }
    return set;
  }, [activeSports, challengerActive, selectedDashboardSport]);

  // Sports the "Hide empty" toolbar control offers (#213): the binary moneyline
  // sports actually on the board — selected ∩ active, with soccer already
  // dropped by selectedMoneylineSports. #212 makes only SELECTED sports eligible
  // for suppression; intersecting with activeSports further keeps the menu from
  // advertising sports with no games today, matching the sport chips' own
  // auto-hide. Empty ⇒ the toolbar renders no control. Depends on activeSports,
  // so it lives below that memo. Canonical order inherited from
  // selectedMoneylineSports.
  const eligibleEmptySports = useMemo<Sport[]>(
    () => selectedMoneylineSports.filter((sport) => activeSports.has(sport)),
    [selectedMoneylineSports, activeSports],
  );
  // Toggle one sport's empty-row suppression. The store setter dedupes and
  // re-orders, so an append that duplicates is harmless.
  const toggleHiddenEmptySport = useCallback(
    (sport: Sport) => {
      setHiddenEmptySports(
        hiddenEmptySports.includes(sport)
          ? hiddenEmptySports.filter((s) => s !== sport)
          : [...hiddenEmptySports, sport],
      );
    },
    [hiddenEmptySports, setHiddenEmptySports],
  );
  // "All sports": select or clear every ELIGIBLE sport, deriving the direction
  // from current membership (no persisted all/none flag, per #212). Sports not
  // eligible right now (configured on another board) are preserved either way,
  // so narrowing the filter never silently un-hides them.
  const toggleAllEmptySports = useCallback(() => {
    const allHidden =
      eligibleEmptySports.length > 0 &&
      eligibleEmptySports.every((sport) => hiddenEmptySports.includes(sport));
    if (allHidden) {
      const eligibleSet = new Set(eligibleEmptySports);
      setHiddenEmptySports(hiddenEmptySports.filter((sport) => !eligibleSet.has(sport)));
    } else {
      setHiddenEmptySports([...hiddenEmptySports, ...eligibleEmptySports]);
    }
  }, [eligibleEmptySports, hiddenEmptySports, setHiddenEmptySports]);

  // #509 — the Kalshi-markets-only filter is scoped to the moneyline board, the
  // same fence #212 drew around empty-row suppression. Scoping is made EXPLICIT
  // here rather than left to fall out of `activityReady` (which is only ever
  // true on a moneyline board because the poly reads are gated that way): an
  // implicit coupling would silently widen the filter the day that gate moves.
  const isMoneylineBoard = selectedMarketType === 'moneyline' && !isGolfMatchBoard;
  // The effective flag the table filters on — the persisted opt-in AND the
  // board being in scope. Off-board the setting is preserved, never cleared.
  const kalshiMarketsOnlyActive = kalshiMarketsOnly && isMoneylineBoard;
  const toggleKalshiMarketsOnly = useCallback(() => {
    setKalshiMarketsOnly(!kalshiMarketsOnly);
  }, [kalshiMarketsOnly, setKalshiMarketsOnly]);

  // ── Search selection (#754) ────────────────────────────────────────
  // How many of the eligible groups are on screen, tied to the query that
  // produced them: a new search starts at the first page again without an
  // effect racing the render that already used the old count.
  // The key is the mode AND the query: switching Today → This week is a new
  // list of games, and inheriting a "Show all" from the previous one would
  // open it fully expanded with no gesture asking for that.
  const shownKey = `${effectiveDiscoveryMode}:${discoveryQuery}`;
  const [shown, setShown] = useState({ key: '', count: DISCOVERY_PAGE_SIZE });
  const visibleCount = shown.key === shownKey ? shown.count : DISCOVERY_PAGE_SIZE;
  const discoveryReading = useMemo(() => discoveryReadingFor({
    query: discoveryQuery,
    mode: effectiveDiscoveryMode,
    marketType: selectedMarketType,
    statusFilter,
    kalshiMarketsOnly: kalshiMarketsOnlyActive,
    allSports,
    selectedSport,
    dateFilter,
    // The window the rows were READ over. Handing the selector the requested
    // window instead would re-filter a fallen-forward board back down to the
    // empty day it fell forward from, discarding every row on screen.
    slate: discoveryWindow,
  }), [discoveryQuery, effectiveDiscoveryMode, selectedMarketType, statusFilter,
    kalshiMarketsOnlyActive, allSports, selectedSport, dateFilter, discoveryWindow]);
  const discoveryGroups = useMemo(
    () => (discoveryActive
      ? buildDiscoveryCatalog({
        moneylines: discoveryMoneylines,
        multi: discoveryMultiEvents,
        lines: discoveryLineEvents,
        futures: discoveryFutureBoards,
      })
      : []),
    [discoveryActive, discoveryMoneylines, discoveryMultiEvents, discoveryLineEvents,
      discoveryFutureBoards],
  );
  const discoverySelection = useMemo(
    () => (discoveryActive
      ? selectDiscoveryGroups(discoveryGroups, discoveryReading, {
        nowMs: boardNow,
        timeZone: discoveryTimeZone,
        week: DISCOVERY_WEEK,
        visibleCount,
      })
      : null),
    [discoveryActive, discoveryGroups, discoveryReading, boardNow, discoveryTimeZone,
      visibleCount],
  );
  /** What the board DRAWS: the page the trader asked for, 20 at a time. */
  const discoveryResults = useMemo(
    () => (discoverySelection ? splitDiscoveryResults(discoverySelection.visible) : null),
    [discoverySelection],
  );
  /**
   * What the board can still FIND: every eligible group, page or no page.
   *
   * Drawing and addressing are two different questions, and answering both
   * with the page closed an open ticket whenever its game slipped off it. In
   * a mode that is not hypothetical — the rank moves on every publication, so
   * a game nobody can even see trading past the twentieth evicted the ticket
   * the trader was working in, on someone else's trade, with the game still
   * eligible and still inside the window.
   *
   * A ticket still closes the moment its game genuinely leaves: settled,
   * filtered out, gone from the feed, or the mode ended. All of those take it
   * out of `groups` too. Only the paging case changes. Nothing new becomes
   * clickable either — a click comes from a drawn row or from the open
   * panel's own ladder, and the panel's game is exactly what this keeps
   * reachable.
   */
  const discoveryAddressable = useMemo(
    () => (discoverySelection ? splitDiscoveryResults(discoverySelection.groups) : null),
    [discoverySelection],
  );
  // What the board says it just searched. The gaps are the point: a trader who
  // finds nothing has to be able to tell "no such market" from "this board
  // cannot see that kind of market".
  const discoveryCoverageSummary = useMemo(
    () => (discoverySelection
      ? discoveryCoverage({
        query: discoveryQuery,
        mode: effectiveDiscoveryMode,
        marketType: selectedMarketType,
        statusFilter,
        dateFilter,
        // The window actually searched, which is the fallback's on a board
        // that fell forward — the scope line must not name a day the results
        // did not come from.
        slate: discoveryWindow,
        // Only Manual can fall forward: a mode reads the days it names, and
        // saying "showing tomorrow instead" under Today would describe a
        // substitution this board does not make.
        fallbackNote: !discoveryModeRange && usingUpcomingFallback && upcomingFallbackSlate
          ? upcomingSlateSearchCopy(slateDates, upcomingFallbackSlate.window, effectiveSlateRange)
          : null,
        allSports: discoverySelection.allSports,
        sportLabel: sportFilterLabel,
        kalshiMarketsOnly: kalshiMarketsOnlyActive,
        total: discoverySelection.total,
        shown: discoverySelection.visible.length,
      })
      : null),
    [discoverySelection, discoveryQuery, effectiveDiscoveryMode, selectedMarketType,
      statusFilter, dateFilter, discoveryWindow, discoveryModeRange, slateDates,
      effectiveSlateRange, usingUpcomingFallback, upcomingFallbackSlate, sportFilterLabel,
      kalshiMarketsOnlyActive],
  );

  // Which control states which half of the coverage. In Manual the search bar
  // is the only scope on screen and carries all of it; under a mode the dates,
  // counts and blind spots belong beside the mode's own controls, where the
  // window they describe is named.
  const discoveryBarCoverage = discoveryCoverageSummary && effectiveDiscoveryMode === 'manual'
    ? discoveryCoverageSummary
    : discoveryCoverageSummary && { ...discoveryCoverageSummary, gaps: [] };
  // What the widened table is a list OF. A committed query is a search
  // whatever mode it runs in; a mode without one is a date scope, and saying
  // "Search results" over it names a gesture the trader never made.
  const discoveryModeLabel = effectiveDiscoveryMode === 'week' ? 'This week' : 'Today';
  const discoveryTableTitle = isDiscoveryActive(discoveryQuery)
    ? 'Search results'
    : `${discoveryModeLabel} — every sport`;
  const discoveryEmptyLabel = `No games ${effectiveDiscoveryMode === 'week' ? 'this week' : 'today'} in any sport`;
  const discoveryModeCoverage = effectiveDiscoveryMode !== 'manual'
    && discoveryCoverageSummary && discoveryCoverageSummary.gaps.length > 0
    ? discoveryCoverageSummary.gaps.join(' ')
    : null;

  // ── The CFB lens (#654) ────────────────────────────────────────────
  // Both halves follow kalshiMarketsOnlyActive's rule: the persisted intent AND
  // the board being in scope. Off-board the settings are preserved, never
  // cleared, so switching sports and back doesn't silently lose them.
  //
  // The minimized filter is board-scoped for a stronger reason than tidiness:
  // the per-row restore control only renders on a lens board, so applying the
  // filter anywhere else would hide a trader's games on a board offering no way
  // to bring them back — exactly what the contract forbids.
  // #1130 — every CFB matchup board, Moneyline and Spread/Total alike. The
  // Spread/Total board carries the #120 bookmark on its own rows and applies
  // the filter to its own read (`savedOnlyForSpreadTotal` below).
  const cfbSavedOnlyEligible = cfbBoardSelected;
  const savedOnlyActive = cfbSavedOnly && cfbSavedOnlyEligible;
  const toggleCfbSavedOnly = useCallback(() => {
    setCfbSavedOnly(!cfbSavedOnly);
  }, [cfbSavedOnly, setCfbSavedOnly]);
  // Memoized, not built in `renderSpreadsTotals`: that board re-derives its
  // rows from this object and reports the expanded game back into this
  // component's state, so a fresh object per render loops the two.
  const savedOnlyForSpreadTotal = useMemo(
    () =>
      savedOnlyActive
        ? {
            isSaved: (eventKey: string) => isRowSaved(eventKey, savedIndex, authUserId),
            totalSaved: savedCount,
            onShowFullBoard: toggleCfbSavedOnly,
          }
        : undefined,
    [savedOnlyActive, savedIndex, authUserId, savedCount, toggleCfbSavedOnly],
  );
  const minimizedLens = useMemo(
    () =>
      cfbBoardSelected
        ? {
            isMinimized: (game: GameRow) =>
              isRowMinimized(game.key, minimizedIndex, authUserId),
            // `toggle` is the restore: the game is minimized (that is why the
            // footer is offering it), so toggling can only clear the mark.
            onRestore: (game: GameRow) => toggleMinimizedGame(game, authUserId),
            onRestoreAll: () => restoreAllMinimized(authUserId),
          }
        : undefined,
    [cfbBoardSelected, minimizedIndex, authUserId, restoreAllMinimized, toggleMinimizedGame],
  );

  // ── Orderbook needs ────────────────────────────────────────────────
  const displayedGames = discoveryResults ? discoveryResults.moneylines : games;
  const displayedMultiEvents = discoveryResults ? discoveryResults.multi : multiEvents;
  const displayedFutureBoards = discoveryResults ? discoveryResults.futures : seriesEvents;
  // Declare the set of tickers this view currently renders depth for.
  // The store unions these with other views' needs and hands the union
  // to HydraClient, which narrows the upstream orderbooks subscription.
  // Recompute from `games` (and the series outcomes when that tab's
  // active); `version` already triggers that memo upstream.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of displayedGames) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
      if (g.polymarket?.away.marketTicker) set.add(g.polymarket.away.marketTicker);
      if (g.polymarket?.home.marketTicker) set.add(g.polymarket.home.marketTicker);
    }
    for (const event of displayedFutureBoards) {
      if (isPolyFutureBoard(event)) continue;
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    for (const event of displayedMultiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
        if (outcome.polymarket?.marketTicker) set.add(outcome.polymarket.marketTicker);
      }
    }
    return Array.from(set);
  }, [displayedGames, displayedFutureBoards, displayedMultiEvents]);

  // Stable key derived from the ticker set so the effect only re-runs when
  // the actual set changes, not on every RAF tick (orderbookTickers gets a
  // fresh array ref every tick because `games` does).
  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  // Kalshi-only ticker subset for the per-user POS hook. We only need
  // queue-position lookups against tickers actually shown on screen — feeding
  // the full set keeps the queue-positions REST call narrow and avoids
  // pulling depth for off-screen orders (e.g. NHL while NBA is selected).
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of displayedGames) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
    }
    for (const event of displayedMultiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    return Array.from(set);
  }, [displayedGames, displayedMultiEvents]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);
  const humanOrdersByTicker =
    userActivity.displayOrdersByTicker ?? userActivity.ordersByTicker;
  // #192/#212 — both poly reads exist ONLY to answer "does the user have
  // exposure on this empty row", so they are gated on the suppression list
  // being ACTIVE for a currently selected moneyline sport, not merely
  // configured. Deselecting or un-hiding must leave no timer behind:
  // revealing rows is the cheap state, and a dashboard parked with no
  // effective suppression should not keep spending the org's relay read
  // budget. Re-activating re-arms both (enabled false → true re-runs each
  // hook's polling effect, which fetches immediately before scheduling the
  // interval).
  const polyActivityEnabled = polyActivityPollingEnabled({
    isMoneylineBoard,
    selectedMoneylineSports,
    hiddenEmptySports,
    kalshiMarketsOnly,
    // #654 — arming the reads is what makes the lens work at all. Without it
    // activityReady never turns true and the filter silently hides nothing,
    // exactly as this module's contract warns. Gated on marks EXISTING, not on
    // the lens merely being available, so a CFB board with an empty mark list
    // spends no read budget.
    cfbMinimizedActive: minimizedLens != null && minimizedIds.size > 0,
  });
  const relayPolyPositions = usePolymarketRelayPositions({
    enabled: polyActivityEnabled,
    includePnl: false,
  });
  const relayPolyOrders = usePolymarketRelayOrders({
    enabled: polyActivityEnabled,
  });
  // #1266 — the always-mounted account summary now needs the existing bounded
  // saved-G1 US read even without a suppression filter. The popup and activity
  // filter share it, never adding another reader. Its existing hidden-tab pause,
  // account-presence check and session fence still apply. Catalogue activity
  // lookups below remain limited to their previous filter/popup demand.
  const usRail = useUsRail(true);
  const [usClassifier] = useState(() => new UsMarketClassifier(createUsBrowserMarketClient()));
  useEffect(() => {
    usClassifier.open();
    return () => usClassifier.close();
  }, [usClassifier]);
  const usClassifierVersion = useSyncExternalStore(usClassifier.subscribe, usClassifier.getVersion);
  // Asked when the read changes, and again every minute while there is one:
  // the rail keeps an unchanged read's identity, so without the timer a
  // lookup that failed once would never be retried (`request` skips placed
  // markets and misses inside their 5 min retry window, so a tick costs
  // nothing until a retry is due). Not while the page is hidden, as the rail.
  useEffect(() => {
    const read = usRail.read;
    if (!read || (!polyActivityEnabled && !showPositionSidebar)) return;
    const ask = () => {
      if (document.hidden) return;
      for (const { account, slugs } of usExposureLookups(read)) usClassifier.request(slugs, account);
    };
    ask();
    const id = window.setInterval(ask, US_BOARD_LOOKUP_RETRY_TICK_MS);
    return () => window.clearInterval(id);
  }, [usClassifier, usRail.read, polyActivityEnabled, showPositionSidebar]);
  const usActivity = useMemo(() => {
    void usClassifierVersion;
    return usBoardActivity(usRail.read, (slug) => usClassifier.lookup(slug));
  }, [usClassifier, usClassifierVersion, usRail.read]);
  const usActivityAuthoritative = usActivityIsAuthoritative(usRail, usActivity);
  // Edge bot presence by ticker (row chips). No-op fetch for non-bot users.
  const edgeBots = useEdgeBotsByTicker();

  // Canonical gameKeys the user has any position in — drives the archived-row
  // left-border highlight. Sourced from the FULL per-user position list
  // (accountStore.positions), NOT the ticker-filtered /me/snapshot poller: the
  // highlight must light archived/past-date rows for ANY game the user holds,
  // even ones with no live visible ticker, and the snapshot poller now only
  // returns the visible-ticker subset. Each ticker is parsed via parseTicker
  // (venue-agnostic so PM-* tickers resolve to the same {game, date} shape),
  // and composed into the same alpha-sorted canonical key the analytics
  // endpoint emits. Recomputes only when the position set changes.
  const accountStatus = useAccountStore((s) => s.status);
  const accountPositions = useAccountStore((s) => s.positions);
  const accountOpenOrders = useAccountStore((s) => s.openOrders);
  const personalPolyStatus = usePolyAccountStore((s) => s.status);
  const personalPolyOrders = usePolyAccountStore((s) => s.openOrders);

  const activeKalshiTickers = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const position of accountPositions) {
      if (position.position > 0) set.add(position.ticker);
    }
    for (const order of accountOpenOrders) {
      if (order.remainingCount > 0) set.add(order.ticker);
    }
    return set;
  }, [accountPositions, accountOpenOrders]);

  const activePolyMarketIds = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const position of relayPolyPositions.positions) {
      if (position.netCount > 0) set.add(position.ticker);
    }
    for (const order of relayPolyOrders.orders) {
      if (typeof order.asset_id === 'string' && order.asset_id) set.add(order.asset_id);
      if (typeof order.market === 'string' && order.market) set.add(order.market);
    }
    for (const order of personalPolyOrders) {
      if (order.assetId) set.add(order.assetId);
      if (order.conditionId) set.add(order.conditionId);
    }
    return set;
  }, [relayPolyPositions.positions, relayPolyOrders.orders, personalPolyOrders]);

  // #192 — an EMPTY activity map only justifies hiding a row when every venue
  // read actually answered; see emptyTennisEvidence.ts for the per-source
  // reasoning (notably: `unavailable` is a non-answer, not a "no").
  const activityReady = activityEvidenceIsAuthoritative({
    accountStatus,
    kalshiActivityLoading: userActivity.loading,
    kalshiActivityError: userActivity.error,
    polyPositionsStatus: relayPolyPositions.status,
    polyOrdersStatus: relayPolyOrders.status,
    personalPolyStatus,
    usActivityAuthoritative,
  });

  const positionGameKeys = usePositionGameKeys(accountPositions, gameIdentityVersion);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('value-dashboard', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('value-dashboard', []);
    };
    // orderbookTickers is intentionally excluded — orderbookTickersKey is the
    // stable identity for the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Batched Unabated gameState poll for the visible game list. Drives
  // live scores in the team-name column + (next step) "finished games
  // drop to bottom" via authoritative statusId rather than the existing
  // status/time/price-collapse heuristics. Falls out cleanly when the
  // game list is empty (poller skips fetches with no keys).
  const dashboardGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const g of displayedGames) {
      // Live poll keys keep the v2 gameNo — an undisambiguated key makes
      // hydra resolve "the live game first", which cross-wires a
      // doubleheader's scores (2026-07-11 MIL@PIT).
      const k = toHydraGameKeyLive(g.key);
      if (k) set.add(k);
    }
    // Multi-outcome (soccer) events too — their pair rows show live scores
    // when Unabated covers the match; misses just render the live dash.
    for (const e of displayedMultiEvents) {
      const k = toHydraGameKeyLive(e.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [displayedGames, displayedMultiEvents]);
  useGameStatesPoll(dashboardGameKeys);

  // ── Detail panel state ──────────────────────────────────────────────
  const [futuresSelection, setFuturesSelection] = useState<FuturesSelection | null>(null);
  const [golfSelection, setGolfSelection] = useState<StandaloneSelection | null>(null);
  const golfBoardScope = JSON.stringify([isGolfMatchBoard, selectedSport, selectedDateIso, dateFilter, statusFilter, authUserId, authOrgId]);
  const [previousGolfBoardScope, setPreviousGolfBoardScope] = useState(golfBoardScope);
  if (previousGolfBoardScope !== golfBoardScope) {
    // Reset before committing children, including Back/Forward navigation.
    // Returning to an earlier scope must not resurrect its old order draft.
    setPreviousGolfBoardScope(golfBoardScope);
    setGolfSelection(null);
  }
  const [tradeIntent, setTradeIntent] = useState<TradeIntent | null>(null);
  const tradeInteractionSequenceRef = useRef(0);

  // ── Spreads & Totals detail panel state ────────────────────────────
  // When the user expands a spread or total row, SpreadsTotals synthesizes
  // a GameRow for that line and feeds it here; the existing detail panel
  // renders against the synth. `stCloseSignal` bumps when the user closes
  // the panel so SpreadsTotals knows to collapse its inline expansion.
  const [spreadTotalBetCtx, setSpreadTotalBetCtx] = useState<BetLineContext | null>(null);
  // Leaving the spread/total tab → drop the synth + chip-strip context.
  // Without this, a totals synth set earlier still drives the side panel
  // on the moneyline tab (expandedGame's null-key path falls back to
  // spreadTotalGame). The panel ends up "stuck" on the prior totals bet
  // until the user explicitly clicks a moneyline pill.
  useEffect(() => {
    if (selectedMarketType !== 'series') setFuturesSelection(null);
    if (selectedMarketType !== 'spread-total') {
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
    }
  }, [selectedMarketType]);
  const [stCloseSignal, setStCloseSignal] = useState(0);

  // #248 — the hydration hook is declared BELOW (it needs handleTradeClick),
  // so the handlers that dismiss a focus reach it through this ref rather
  // than being hoisted past their own dependencies.
  const dismissFocusRef = useRef<() => void>(() => {});
  /** A deliberate board move: dismiss the focus AND stop hydrating for the
   *  rest of this mount. Feed ticks and price-mode toggles must not call it —
   *  they aren't the trader changing context. */
  const takeOverFocus = useCallback(() => {
    dismissFocusRef.current();
    setFocusTakenOver(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    dismissFocusRef.current();
    setExpandedGameKey(null);
    setTradeIntent(null);
    setFuturesSelection(null);
    setGolfSelection(null);
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
    setStCloseSignal((c) => c + 1);
  }, []);

  // ── Search gestures (#754) ─────────────────────────────────────────
  // Committing normalises through the selection contract's own helper, so the
  // board and the selector can never disagree about what "the same query" is.
  // Neither gesture selects a result. Leaving search discards only a ticket
  // whose result is hidden by the restored manual board.
  const handleSearchCommit = useCallback((next: string) => {
    takeOverFocus();
    const committed = commitDiscoveryQuery(discoveryReading, next).query;
    // Back to the first page. Keying the count on the query TEXT alone let a
    // re-search of the same words inherit a previous "Show all" — the second
    // search would open fully expanded with no gesture asking for it.
    setShown({ key: `${effectiveDiscoveryMode}:${committed}`, count: DISCOVERY_PAGE_SIZE });
    setDiscoveryQuery(committed);
  }, [takeOverFocus, setDiscoveryQuery, discoveryReading, effectiveDiscoveryMode]);
  const handleSearchClear = useCallback(() => {
    // Only the query clears — the interaction contract's own rule. A mode the
    // trader chose is not a search and survives one being abandoned; the
    // sport, date, status and venue scope underneath are exactly what they
    // return to, because the search never wrote over them in the first place.
    setShown({ key: `${effectiveDiscoveryMode}:`, count: DISCOVERY_PAGE_SIZE });
    if (discoveryQuery !== '') {
      setDiscoveryQuery('');
    }
  }, [discoveryQuery, setDiscoveryQuery, effectiveDiscoveryMode]);
  /**
   * #754/#902 — leave discovery altogether: the query AND the mode.
   *
   * This is the "explicit manual sport/date gesture" row of the interaction
   * contract. Naming a sport or a date IS the trader stating their own scope,
   * so an all-sport Today sitting on top of it is over, not merely narrowed.
   * Distinct from `handleSearchClear` above, which the search box's own Clear
   * and Escape use and which must leave a chosen mode standing.
   */
  const handleDiscoveryExit = useCallback(() => {
    setShown({ key: 'manual:', count: DISCOVERY_PAGE_SIZE });
    updateReading((previous) => (previous.discoveryQuery === '' && previous.discoveryMode === 'manual'
      ? previous
      : { ...previous, discoveryQuery: '', discoveryMode: 'manual' }));
  }, [updateReading]);
  /**
   * #902 — where focus goes when the control that was pressed disappears.
   *
   * "Show all N more" removes itself by succeeding, and "Back to my board"
   * takes the whole scope line with it. Both left a keyboard trader on
   * `document.body`, at the top of a board they had just lengthened or
   * replaced. Focus moves to the mode the board is NOW in — always mounted,
   * the control they acted through, and it states where they ended up. With
   * no mode controls on the board (Futures), the search box is the stable
   * thing that remains.
   *
   * Requested through a counter rather than called inline: the button is
   * still there during the click, and the destination is only correct after
   * the render that removed it.
   */
  const discoveryHeaderRef = useRef<HTMLDivElement>(null);
  const discoveryControlsRef = useRef<HTMLDivElement>(null);
  const [discoveryFocusRequest, setDiscoveryFocusRequest] = useState(0);
  useEffect(() => {
    if (discoveryFocusRequest === 0) return;
    const controls = discoveryControlsRef.current;
    // The mode now in force. Back on the trader's own board nothing is
    // pressed, and the group's first button is "Today" — landing focus there
    // after a MANUAL Show all puts the next Enter on a gesture that widens
    // the board to every sport, which is not what they asked for. The search
    // box is where a manual search's own controls are, so that is where it
    // goes; the group's first button remains the last resort for a mode board
    // whose pressed button somehow went missing.
    const pressedMode = controls?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]');
    const target = pressedMode
      ?? discoveryHeaderRef.current?.querySelector<HTMLInputElement>('input[type="text"]')
      ?? controls?.querySelector<HTMLButtonElement>('button');
    target?.focus();
  }, [discoveryFocusRequest]);
  const handleShowAllResults = useCallback(() => {
    setShown({ key: shownKey, count: Number.MAX_SAFE_INTEGER });
    setDiscoveryFocusRequest((n) => n + 1);
  }, [shownKey]);
  /**
   * Today / This week. The saved manual scope is deliberately left untouched:
   * `discoveryReadingFor` keeps it in `manualSports` / `manualRange`, so the
   * way back is a restore by not having changed anything.
   */
  const handleDiscoveryModeChange = useCallback((mode: DiscoveryMode) => {
    takeOverFocus();
    setShown({ key: `${mode}:${discoveryQuery}`, count: DISCOVERY_PAGE_SIZE });
    setDiscoveryMode(mode);
  }, [takeOverFocus, setDiscoveryMode, discoveryQuery]);
  /** "Back to my board" — the mode ends and the query with it, with no ticket
   *  or arming change. Same gesture as an explicit manual sport/date choice,
   *  minus naming a new one. */
  const handleReturnToManual = useCallback(() => {
    takeOverFocus();
    handleDiscoveryExit();
    setDiscoveryFocusRequest((n) => n + 1);
  }, [takeOverFocus, handleDiscoveryExit]);


  const handleToggleExpand = useCallback(
    (key: string, side: TeamSide) => {
      // #248 — the trader is opening a ticket of their own. A focus still
      // waiting on its row must not land on top of it ten seconds later:
      // swapping the panel out from under someone mid-interaction is the
      // surprise the frozen-timeout rule exists to prevent. Dismiss, don't
      // take over — a row click is not a board move, so a genuinely NEW
      // focused link arriving later must still be allowed to hydrate.
      dismissFocusRef.current();
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
      if (expandedGameKey === key) {
        if (tradeIntent?.side === side) {
          setExpandedGameKey(null);
          setTradeIntent(null);
        } else {
          setTradeIntent({ side, venue: 'kalshi' });
        }
        return;
      }
      setExpandedGameKey(key);
      setTradeIntent({ side, venue: 'kalshi' });
    },
    [expandedGameKey, tradeIntent?.side],
  );

  // Live-trading one-click mode (V1b) — armed state + submit lane consumed by
  // the user click handler below; the toolbar control owns arming/size UI.
  const oneClickArmed = useOneClickStore((s) => s.armed);
  const disarmOneClick = useOneClickStore((s) => s.disarm);
  const handleFutureSelect = useCallback((selection: FuturesSelection) => {
    handleCloseDetail();
    disarmIfArmed(DISARM_REASON.futures);
    setFuturesSelection(selection);
  }, [handleCloseDetail]);
  const oneClickAccess = useOneClickAccess();
  // Layout only — the cell keeps its place while a restored grant is confirmed.
  const oneClickSlot = useGrantSlot('oneClickAccess');
  const handleGolfSelect = useCallback((selection: StandaloneSelection) => {
    handleCloseDetail();
    disarmIfArmed(DISARM_REASON.golf);
    setGolfSelection(selection);
  }, [handleCloseDetail]);
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);

  // D5 — losing confirmed one-click access keeps the toolbar cell, status
  // line only, until the trader dismisses it; one-click turns off with the
  // revocation sentence as a manual disarm. Before the context disarm below,
  // so its reason is the one the strip keeps.
  const lt2ReadoutShown = useLt2ReadoutSlotShown();
  const oneClickRevocation = useOneClickRevocation({
    access: oneClickAccess,
    accessKnownOff: oneClickSlot === 'hidden',
    identity: `${authUserId ?? ''}|${authOrgId ?? ''}`,
    reason: ONE_CLICK_REVOKED_REASON,
    frame: lt2ReadoutShown,
  });

  // Armed trading never survives an identity or board-context boundary.
  // Disarming after the commit preserves rapid clicks inside one stable board
  // while disarming on account/org changes, date, sport, market-kind, or
  // YES/NO context changes. D5 — each names its cause on the strip, and only
  // an armed mode is turned off (an off strip keeps the reason it shows).
  const boardDisarmContext: DisarmContextField[] = [
    { value: authUserId, reason: DISARM_REASON.account },
    { value: authOrgId, reason: DISARM_REASON.org },
    { value: selectedSport, reason: DISARM_REASON.sport },
    { value: selectedSportsKey, reason: DISARM_REASON.sport },
    { value: selectedMarketType, reason: DISARM_REASON.marketType },
    { value: selectedDate, reason: DISARM_REASON.date },
    { value: marketSide, reason: DISARM_REASON.side },
  ];
  const boardDisarmContextRef = useRef<DisarmContextField[] | null>(null);
  useEffect(() => {
    const reason = changedContextReason(boardDisarmContextRef.current, boardDisarmContext);
    boardDisarmContextRef.current = boardDisarmContext;
    if (reason != null) disarmIfArmed(reason);
    // Fail closed: armed without confirmed access (not a revocation, which
    // the hook above has already handled) turns off too.
    else if (!oneClickAccess) disarmIfArmed(DISARM_REASON.accessUnconfirmed);
    // The context values are the deps; the array is rebuilt from them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    oneClickAccess,
    authUserId,
    authOrgId,
    selectedSport,
    selectedSportsKey,
    selectedMarketType,
    selectedDate,
    marketSide,
  ]);
  // Route departure.
  useEffect(() => () => disarmIfArmed(DISARM_REASON.leftDashboard), []);

  /**
   * #730 — the row click handlers read their trading context through this
   * ref instead of closing over it, so their identity never moves and a
   * memoised row is not re-rendered by a same-sport update to a DIFFERENT
   * game. Freshness is the whole question, and the answer is WHEN the ref is
   * written: `useLayoutEffect` runs synchronously inside React's commit,
   * before control returns to the event loop, so no click can be dispatched
   * between a commit and this write — a handler always sees the latest
   * COMMITTED `games` (identity-stabilised above) and market side. Not a
   * passive effect (a click could land in that gap) and not a render-phase
   * ref write (which the repository's React rules forbid). Same discipline as
   * `useLt2Card`'s resolver ref. Pinned by tradeClickFreshness.test.tsx,
   * which clicks immediately after a book publication and asserts the ticket
   * carries the new price.
   */
  // #754 — what a click can resolve to. Identical to `games` BY REFERENCE
  // whenever no query is committed, so every existing trade path is reached
  // through exactly the array it was reached through before.
  const selectableGames = discoveryAddressable ? discoveryAddressable.moneylines : games;
  const tradeCtxRef = useRef({
    games: selectableGames, marketSide, setMarketSide, oneClickAccess, oneClickArmed, disarmOneClick,
  });
  useLayoutEffect(() => {
    tradeCtxRef.current = {
      games: selectableGames, marketSide, setMarketSide, oneClickAccess, oneClickArmed, disarmOneClick,
    };
  });

  const handleTradeClick = useCallback(
    (
      gameKey: string,
      side: 'away' | 'home',
      venue: 'kalshi' | 'polymarket',
      opts?: {
        priceCents?: number;
        initialOrderSide?: 'yes' | 'no';
        postOnly?: boolean;
        interactionSequence?: number;
        liquiditySeed?: BookLiquiditySeed | null;
      },
    ) => {
      const ctx = tradeCtxRef.current;
      const game = ctx.games.find((g) => g.key === gameKey);
      if (!game) return;

      const initialOrderSide = opts?.initialOrderSide ?? ctx.marketSide;
      const book =
        side === 'away'
          ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
          : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book, initialOrderSide);

      // Trade modal was removed 2026-05-22 (deleted 2026-07-10) — the
      // GameDetailPanel side panel is the single trade-ticket surface
      // now. setExpandedGameKey opens the panel; tradeIntent carries the
      // click's pre-fill data so the ticket lands ready-to-submit.
      //
      // Clearing spread/total state on a moneyline-pill click is
      // belt-and-suspenders: even though `expandedGame` already prefers
      // the moneyline game when expandedGameKey resolves, the panel's
      // betLineContext check is identity-based and any stale
      // spreadTotalGame ref hanging around made the totals panel
      // "stick" after the user moved to a moneyline bet. Wipe both so
      // the panel switches cleanly into moneyline mode.
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
      setExpandedGameKey(gameKey);
      setTradeIntent({
        side,
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
        // The price is this moneyline's. The dashboard keeps this intent when
        // the panel moves to a spread or total row (a market-type switch, a
        // line row expanded without a pill click); untagged, the remounted
        // panel wrote it into that line's ticket (TradeIntent.gameKey).
        gameKey,
        interactionSequence: opts?.interactionSequence,
        liquiditySeed: opts?.liquiditySeed,
      });
      // Pill click also flips the page-level YES/NO toggle to match.
      // Default is NO, so this only triggers when a trader clicks a
      // green YES pill — the table + panel then read consistently
      // from the toolbar's YES state instead of asking the user to
      // toggle separately.
      if (opts?.initialOrderSide && opts.initialOrderSide !== ctx.marketSide) {
        // `setMarketSide` is a reading-view setter (#826), not a stable
        // useState setter: read it from the ref too, or a click after a
        // history change writes through a superseded one and the toolbar
        // silently stops following pills.
        ctx.setMarketSide(opts.initialOrderSide);
      }
    },
    [],
  );

  /** The pill / ladder click path the tables call. Identical to
   *  `handleTradeClick` except that it first cancels a focus still waiting on
   *  its row — same reasoning as `handleToggleExpand`. Hydration deliberately
   *  calls the RAW `handleTradeClick` below, so this dismissal is reachable
   *  only from a genuine user click and can never cancel the open it is about
   *  to perform.
   *
   *  One-click interception (live-tennis V1b): while the toolbar control is
   *  ARMED, a genuine Kalshi price click submits immediately through
   *  oneClickStore (same audited placeOrder lane) instead of opening the
   *  ticket. Deliberately gated on `opts.priceCents` — side clicks with no
   *  price still open the panel — and on this USER handler only, so deep-link
   *  hydration (raw handleTradeClick) can never place an order. */
  const handleUserTradeClick = useCallback(
    (...args: Parameters<typeof handleTradeClick>) => {
      const [gameKey, side, venue, opts] = args;
      const ctx = tradeCtxRef.current;
      if (ctx.oneClickAccess && ctx.oneClickArmed && venue === 'kalshi' && opts?.priceCents != null) {
        const game = ctx.games.find((g) => g.key === gameKey);
        if (game) {
          // D-020: the shared moneyline adapter REVALIDATES freshness /
          // availability / maker-role against the current book at submit
          // time, so an armed click racing a stale flip or a book move gets
          // a truthful local rejection instead of a blind submit. It also
          // owner-gates the hotkey-readiness blur: only a consumed click on
          // the currently focused card releases the pill's DOM focus.
          const consumed = tryOneClickFromMoneylineCtx({
            scope: LT2_DASH_ML_CARD_SCOPE,
            ctx: {
              game,
              teamSide: side,
              venue,
              initialOrderSide: opts.initialOrderSide ?? ctx.marketSide,
              prefillPrice: opts.priceCents,
              // Ladder bid clicks arrive postOnly=true (join, can't cross);
              // ask clicks may cross. Forward the click site's derivation.
              postOnly: opts.postOnly ?? false,
            },
          });
          if (consumed) return;
        }
      }
      // Leaving the eligible hot-click path is itself a safety boundary. A
      // Polymarket click or a non-price row opens the normal ticket and leaves
      // the page disarmed when the trader returns to Kalshi.
      if (ctx.oneClickArmed) ctx.disarmOneClick(ticketClickReason(venue));
      dismissFocusRef.current();
      handleTradeClick(gameKey, side, venue, opts?.priceCents != null
        ? { ...opts, interactionSequence: ++tradeInteractionSequenceRef.current }
        : opts);
    },
    [handleTradeClick],
  );

  // ── Polymarket US on the board (#987) ──────────────────────────────
  // US moneyline quotes for supported game boards, read only while the page is
  // visible. Like Kalshi/Polymarket prices they show whether or not this
  // organization has a US account (account-free public reads). A US pill opens
  // the same Game Detail panel on its US tab; it never touches the K/P ticket.
  const usSessionEpoch = useSyncExternalStore(subscribeAuthEpoch, getAuthSessionEpoch);
  const pageVisible = useSyncExternalStore(subscribePageVisibility, readPageVisibility);
  const usBoardSports = useMemo<readonly UsGameSport[]>(
    () => {
      if (allSports) return US_GAME_SPORTS;
      const sport = usGameSportForBoard(selectedSport);
      return sport ? [sport] : [];
    },
    [allSports, selectedSport],
  );
  // The shared per-login presence read: a trader known to have no US account
  // gets no price-seeded US ticket from a pill (D11: unknown or failed is not
  // "no US"). Quotes do not depend on it.
  const usPresence = useUsAccountPresence(usBoardSports.length > 0);
  const usQuotes = useUsBoardQuotes({
    sports: usBoardSports,
    pageVisible,
    sessionEpoch: usSessionEpoch,
  });
  const handleUsTradeClick = useCallback<UsTradeClick>((gameKey, side, opts) => {
    const ctx = tradeCtxRef.current;
    if (!ctx.games.some((g) => g.key === gameKey)) return;
    // Same safety boundary as a Polymarket click: leaving the Kalshi hot-click
    // path disarms one-click, so the next Kalshi pill opens a ticket instead
    // of firing an order.
    if (ctx.oneClickArmed) ctx.disarmOneClick(DISARM_REASON.usPrice);
    dismissFocusRef.current();
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
    setExpandedGameKey(gameKey);
    setTradeIntent({
      side,
      // Untouched by a US intent; named only because the field is required.
      venue: 'polymarket',
      polymarketUs: true,
      // Without a US account Game Detail shows US read-only (no ticket), so no
      // price is carried as a ticket hint.
      prefillPrice: usPillPrefill(usPresence, opts.priceCents),
      initialOrderSide: opts.initialOrderSide,
      postOnly: false,
      // The pill's price is this moneyline's: once the panel moves to another
      // line (a spread or total row, with this intent kept), it is not a hint
      // for that line's US market (TradeIntent.gameKey).
      gameKey,
      interactionSequence: ++tradeInteractionSequenceRef.current,
    });
    if (opts.initialOrderSide !== ctx.marketSide) ctx.setMarketSide(opts.initialOrderSide);
  }, [usPresence]);
  // #1231 — a US pill on a Spread/Total line. Shaped like handleUsTradeClick:
  // never the one-click intercept, disarms an armed page, opens the panel's US
  // tab for the row's exact line (the synthesized line game), price as a hint
  // only. The side panel is already on that line (onActiveGameChange).
  const handleUsSpreadTotalTradeClick = useCallback((data: TradeTicketContext) => {
    const ctx = tradeCtxRef.current;
    if (ctx.oneClickArmed) ctx.disarmOneClick(DISARM_REASON.usPrice);
    setTradeIntent({
      side: data.teamSide,
      // Untouched by a US intent; named only because the field is required.
      venue: 'polymarket',
      polymarketUs: true,
      prefillPrice: usPillPrefill(usPresence, data.prefillPrice),
      initialOrderSide: data.initialOrderSide,
      postOnly: false,
      gameKey: data.game.key,
      interactionSequence: ++tradeInteractionSequenceRef.current,
    });
    if (data.initialOrderSide && data.initialOrderSide !== ctx.marketSide) ctx.setMarketSide(data.initialOrderSide);
  }, [usPresence]);

  // #1150 — Novig board pills. Null seam (production today) passes nothing to
  // the board, so no Novig column renders.
  const novigSeam = useNovigVenueSeam();
  const handleNovigTradeClick = useCallback((gameKey: string, side: TeamSide, click: NovigBoardClick) => {
    const ctx = tradeCtxRef.current;
    if (!ctx.games.some((g) => g.key === gameKey)) return;
    // Leaving the Kalshi hot-click path disarms one-click, as US/Poly clicks do.
    if (ctx.oneClickArmed) ctx.disarmOneClick(DISARM_REASON.novigPrice);
    dismissFocusRef.current();
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
    setExpandedGameKey(gameKey);
    setTradeIntent({
      side,
      // Untouched by a Novig intent; named only because the field is required.
      venue: 'polymarket',
      novigOutcomeId: click.outcomeId,
      novigProbability: click.probability ?? undefined,
      gameKey,
      interactionSequence: ++tradeInteractionSequenceRef.current,
    });
  }, []);

  // ── Focused deep-link hydration (#248) ─────────────────────────────
  // Below handleTradeClick on purpose: the focused link opens the SAME panel
  // a pill click opens, through the same handler, with the same audited
  // downstream path. There is no second order-entry route.
  const openFocusedMatch = useCallback<UseFocusedDashboardHydrationInput['openMatch']>(
    (match, target) => {
      // No price is calculated here — the URL layer never carries one, and
      // handleTradeClick derives the current book price itself.
      handleTradeClick(match.gameKey, match.teamSide, match.venue, {
        initialOrderSide: target.side,
        postOnly: false,
      });
    },
    [handleTradeClick],
  );

  // Polymarket's native YES token for the exact ticker, resolved from the live
  // market frame — never from the URL (#92's fail-closed orientation: a token
  // in a shareable link is an identity we can't verify). Undefined for Kalshi
  // targets, and undefined until the frame lands, which the matcher treats as
  // "cannot prove" rather than "guess".
  const getMarket = useMarketStore((s) => s.getMarket);
  const focusPolymarketNativeYesTokenId = useMemo(() => {
    void boardRevKey;
    if (parsedFocus.kind !== 'market' || parsedFocus.target.venue === 'kalshi') return undefined;
    return getMarket(parsedFocus.target.ticker)?.yesTokenId ?? undefined;
  }, [parsedFocus, getMarket, boardRevKey]);

  // #539 — a focused link naming a spread or total is resolved by the
  // Spread/Total board (which already holds its rows memoized) rather than
  // here, and reports its verdict back through this state. `undefined` for a
  // moneyline target, which is the signal for the hook to match `games` itself.
  const [lineFocusResolution, setLineFocusResolution] =
    useState<ExternalFocusResolution>('searching');
  const focusIsLineMarket =
    parsedFocus.kind === 'market' &&
    focusedBoardFor(parsedFocus.target.ticker) === 'spread-total';
  /** The one target handed to the Spread/Total board — or null when there is
   *  nothing for it to resolve. Derived ONCE so every focus prop below is
   *  gated identically: an earlier version gated the ticker on `focusTakenOver`
   *  but not the event/venue/side, which was harmless only by accident (the
   *  board early-returns without a ticker) and is exactly the kind of
   *  inconsistency that later grows into a bug. */
  const lineFocusTarget =
    focusIsLineMarket && !focusTakenOver && parsedFocus.kind === 'market'
      ? parsedFocus.target
      : null;
  // A new focus URL restarts the search. Without this a second link would
  // inherit the previous link's `opened` and skip hydration entirely.
  const focusIdentity = parsedFocus.kind === 'none' ? '' : parsedFocus.identity;
  useEffect(() => {
    setLineFocusResolution('searching');
  }, [focusIdentity]);

  const focusHydration = useFocusedDashboardHydration({
    focus: focusTakenOver ? NO_FOCUS : parsedFocus,
    connected,
    games,
    polymarketNativeYesTokenId: focusPolymarketNativeYesTokenId,
    externalResolution: focusIsLineMarket ? lineFocusResolution : undefined,
    openMatch: openFocusedMatch,
  });
  const dismissFocus = focusHydration.dismiss;
  useEffect(() => {
    dismissFocusRef.current = dismissFocus;
  }, [dismissFocus]);

  const handleMultiOutcomeClick = useCallback(
    (
      event: MultiOutcomeEvent,
      outcome: SeriesOutcome,
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
    ) => {
      const initialOrderSide: MarketSide = opts?.initialOrderSide ?? marketSide;
      const consumed = tryOneClickFromMultiOutcomePrice({
        outcome,
        venue,
        side: initialOrderSide,
        priceCents: opts?.priceCents,
        postOnly: opts?.postOnly ?? false,
      });
      if (consumed) return;

      // Same safety boundary as the binary table: leaving an eligible armed
      // Kalshi price path for a row, Polymarket, or ordinary ticket action
      // disarms before continuing.
      if (oneClickArmed) disarmOneClick(ticketClickReason(venue));

      const game = synthesizeFromMultiOutcome(event, outcome);
      // Row click (no pill opts) on the already-open outcome toggles it
      // closed — mirrors handleToggleExpand on the binary table.
      if (!opts && expandedGameKey === game.key) {
        handleCloseDetail();
        return;
      }
      const book = venue === 'kalshi' ? outcome.kalshi : outcome.polymarket;
      // Same side semantics as the binary table: a row expand inherits the
      // toolbar's current YES/NO; only an explicit Y/N pill (or ladder)
      // click carries its own side. (The old card-grid UI defaulted row
      // clicks to YES and flipped the toolbar — jarring once soccer rows
      // looked and behaved like every other game row.)
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book ?? undefined, initialOrderSide);
      setSpreadTotalGame(game);
      setSpreadTotalBetCtx(buildMultiOutcomeBetContext(event, outcome));
      setExpandedGameKey(game.key);
      setTradeIntent({
        side: 'away',
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
        gameKey: game.key,
        interactionSequence:
          opts?.priceCents != null ? ++tradeInteractionSequenceRef.current : undefined,
      });
      // Toolbar follows explicit pill clicks only — same rule as
      // handleTradeClick on the binary table.
      if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
        setMarketSide(opts.initialOrderSide);
      }
    },
    [marketSide, setMarketSide, expandedGameKey, handleCloseDetail, oneClickArmed, disarmOneClick],
  );

  const expandedGame = useMemo(() => {
    if (expandedGameKey) {
      // expandedGameKey may be stale across market-type switches (e.g.
      // user clicked a moneyline row, then switched to spread/total
      // tab — the moneyline key won't appear in the new `games` list).
      // Don't return null in that case; fall through to spreadTotalGame
      // so the panel still opens for a fresh spread/total row click.
      // #754 — a committed search replaces the board's rows, so selection has
      // to resolve against the results. Idle, `discoveryAddressable` is null
      // and this is the same array the board has always searched.
      const pool = discoveryAddressable ? discoveryAddressable.moneylines : games;
      const fromGames = pool.find((g) => g.key === expandedGameKey);
      if (fromGames) return fromGames;
      // Multi-outcome (soccer) synth keys aren't in `games` — re-derive the
      // synth fresh from the live event each tick so the panel's books track
      // the feed (the panel renders straight off the game prop; the stored
      // spreadTotalGame below is a click-time snapshot). Falls through to the
      // snapshot when the event leaves the store (e.g. post-settle prune).
      const sep = expandedGameKey.indexOf('|multi|');
      if (sep >= 0) {
        const eventKey = expandedGameKey.slice(0, sep);
        const code = expandedGameKey.slice(sep + '|multi|'.length);
        const events = discoveryAddressable ? discoveryAddressable.multi : multiEvents;
        const event = events.find((e) => e.key === eventKey);
        const outcome = event?.outcomes.find((o) => o.code === code);
        if (event && outcome) return synthesizeFromMultiOutcome(event, outcome);
      }
    }
    // #754 — a committed search or a mode replaces the board with results, so
    // a synth retained from before it is not a fallback: the fall-through
    // below put the previously expanded spread/total or soccer ticket under
    // whichever result row was clicked next, with that row drawn as the
    // expanded one.
    //
    // On a SPREAD/TOTAL board the synth is not a leftover — it is how that
    // board states its open ticket at all, pushed up by `onActiveGameChange`
    // and re-synthesized from live rows on every publication. So it is kept,
    // but only by exact identity: its event has to still be one the results
    // admit. `discoveryAddressable` rather than the drawn page, for the same
    // reason the moneyline lookup uses it — a game trading past the twentieth
    // is not a reason to close a ticket the trader is working in.
    //
    // A ticket still closes the moment its event genuinely leaves: settled,
    // filtered out, gone from the feed, or the query no longer matching it.
    // All of those take it out of the addressable set too.
    if (discoveryAddressable) {
      const eventKey = spreadTotalGame ? synthEventKey(spreadTotalGame.key) : null;
      if (!eventKey) return null;
      return discoveryAddressable.lines.some((event) => event.key === eventKey)
        ? spreadTotalGame
        : null;
    }
    // SpreadsTotals synthesizes a GameRow for the active expanded line
    // and pushes it here via onActiveGameChange. Same detail panel
    // renders against either source.
    return spreadTotalGame;
  }, [expandedGameKey, games, spreadTotalGame, multiEvents, discoveryAddressable]);

  const futureSelectionVisible = futuresSelection !== null && (discoveryAddressable
    ? discoveryAddressable.futures.some((board) => board.key === futuresSelection.boardKey
      && board.sport === futuresSelection.sport
      && (isPolyFutureBoard(board) ? 'polymarket' : 'kalshi') === futuresSelection.venue
      && futuresRows(board).some((row) => row.ticker === futuresSelection.ticker))
    : futuresSelection.sport === selectedSport);

  const [wasSearching, setWasSearching] = useState(discoveryActive);
  const leftSearch = wasSearching && !discoveryActive;
  if (wasSearching !== discoveryActive) setWasSearching(discoveryActive);
  // Check the newly rendered manual scope, not the handler's old scope: date
  // arrows, Today, Reset and route changes can all end a search. Keep a draft
  // when its exact target remains visible; otherwise discard before paint.
  const manualSelectionVisible = leftSearch && (
    (selectedMarketType === 'moneyline' && !isGolfMatchBoard && (
      games.some((game) => game.key === expandedGameKey)
      || multiEvents.some((event) => event.outcomes.some((outcome) =>
        `${event.key}|multi|${outcome.code}` === expandedGameKey))))
    || (selectedMarketType === 'series' && futuresSelection && seriesEvents.some((board) =>
      board.key === futuresSelection.boardKey && board.sport === futuresSelection.sport
      && (isPolyFutureBoard(board) ? 'polymarket' : 'kalshi') === futuresSelection.venue
      && futuresRows(board).some((row) => row.ticker === futuresSelection.ticker)))
    // #754 — the spread/total board answers this one itself. Its open ticket
    // is a synth it re-derives from the rows it has just read back, firing
    // `onActiveGameChange` with the live row or with null when the event is
    // not on the restored board — the same re-resolution it has always done
    // when a date change moved its rows. Deciding here instead would need
    // that board's rows, which only it holds, and clearing regardless would
    // close a ticket whose event is still right there on the board returned to.
    || selectedMarketType === 'spread-total'
  );

  // Adjust this component's selection before committing children, as with
  // golfBoardScope above. An effect would schedule an extra render after the
  // result had already disappeared. Clearing both keys ends this condition.
  // #754 — deliberately no clause for the spread/total synth. It looked
  // necessary (that ticket is held by the pushed synth, not by
  // `expandedGameKey`) and it is not: `expandedGame` already refuses a synth
  // whose event the results no longer admit, so the panel is gone in the same
  // render, and `SpreadsTotals` clears the synth itself on its next — it
  // re-resolves its expanded row against the rows it was handed, and a board
  // that unmounted comes back with none. Adding a clause here would be a
  // guard that guards nothing; the two cases are pinned in the journey.
  if ((leftSearch && !manualSelectionVisible)
    || (discoveryActive && ((expandedGameKey && !expandedGame)
      || (futuresSelection && !futureSelectionVisible)))) {
    setExpandedGameKey(null);
    setTradeIntent(null);
    setFuturesSelection(null);
    setGolfSelection(null);
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
  }

  const seriesTicker = useMemo(() => {
    if (selectedMarketType === 'moneyline' && selectedSports.size !== 1) {
      return '';
    }
    const map: Record<string, string> = {
      nba: 'KXNBAGAME',
      wnba: 'KXWNBAGAME',
      nhl: 'KXNHLGAME',
      cbb: 'KXNCAAMBGAME',
      cfb: 'KXNCAAFGAME',
      atp: 'KXATPMATCH / KXITFMATCH',
      wta: 'KXWTAMATCH / KXITFWMATCH',
      mlb: 'KXMLBGAME',
      ufc: 'KXUFCFIGHT',
      esports: 'KXCS2GAME / KXDOTA2GAME / KXVALORANTGAME / KXLOLGAME',
      boxing: 'KXBOXING',
      cricket: 'KXWT20MATCH',
      soccer: 'KXMLSGAME / KXEPLGAME / KXUCLGAME / KXUELGAME',
    };
    return map[selectedSport] ?? '';
  }, [selectedSport, selectedMarketType, selectedSports.size]);

  // ── Date navigation ─────────────────────────────────────────────────
  // The prev/next buttons always force single-day — i.e. bounded — mode;
  // navigating while filtered to "all" is meaningless (the filter ignores the
  // date entirely). One press moves one whole window: a day, or a week in
  // #712's weekly slate. The ANCHOR is what moves, so it keeps its weekday
  // offset inside the window and switching modes stays lossless.
  const stepSlate = useCallback(
    (delta: number) => {
      takeOverFocus();
      // #754 — an explicit date gesture is the trader stating their own scope,
      // which ends the search and any all-sport mode rather than being applied
      // on top of them.
      updateReading((previous) => ({
        ...previous,
        discoveryQuery: '',
        discoveryMode: 'manual',
        // Stepping onto the current day stores the relative intent again, so a
        // board walked back to today does not decay into a frozen date.
        dateAnchor: toDateAnchor(
          shiftSlate(resolveDateAnchor(previous.dateAnchor), effectiveSlateRange, delta),
        ),
        dateFilter: 'today',
      }));
    },
    [takeOverFocus, effectiveSlateRange, updateReading],
  );

  const handleDatePrev = useCallback(() => stepSlate(-1), [stepSlate]);

  const handleDateNext = useCallback(() => stepSlate(1), [stepSlate]);

  // The remaining toolbar controls that mean "I'm driving now" (#248). The
  // raw setters are no longer passed straight to DashboardToolbar: a focused
  // link must not re-assert its date or market type over a deliberate change.
  const handleDateChange = useCallback(
    (next: string) => {
      takeOverFocus();
      handleDiscoveryExit();
      setSelectedDate(next);
    },
    // #826 — the reading setters are NOT stable across the mount the way the
    // useState setters they replaced were: they close over the current history
    // entry, and a setter captured on an older one is refused. Every handler
    // below therefore depends on the setter it calls.
    [takeOverFocus, handleDiscoveryExit, setSelectedDate],
  );

  const handleDateFilterChange = useCallback(
    (next: DateFilter) => {
      takeOverFocus();
      handleDiscoveryExit();
      setDateFilter(next);
    },
    [takeOverFocus, handleDiscoveryExit, setDateFilter],
  );

  const handleSlateRangeChange = useCallback(
    (next: SlateRange) => {
      takeOverFocus();
      // A slate range forces bounded mode below, which makes this a date
      // gesture by the interaction contract's own definition — and every other
      // one of those ends the search rather than being applied on top of it.
      handleDiscoveryExit();
      setSlateRange(next);
      // Bounded mode, for the same reason prev/next forces it: a slate range
      // describes how wide the bounded view is, and means nothing under "All".
      setDateFilter('today');
    },
    [takeOverFocus, handleDiscoveryExit, setSlateRange, setDateFilter],
  );

  const handleMarketTypeChange = useCallback(
    (next: MarketType) => {
      takeOverFocus();
      if (next !== selectedMarketType && (next === 'series' || selectedMarketType === 'series')) handleCloseDetail();
      if (next !== 'series' && STANDALONE_SPORTS.has(selectedSport)
        && !(selectedSport === 'golf' && next === 'moneyline' && golfMatchesAvailable)) {
        // The explicit move back to games restores the user's landing board,
        // not an empty Golf moneyline with no visible selected sport chip.
        const landing = STANDALONE_SPORTS.has(underlyingSport(defaultDashboardSport))
          ? SPORT_TABS[0].id : defaultDashboardSport;
        setSearchParams((previous) => {
          const params = new URLSearchParams(previous);
          params.set('sport', landing);
          return params;
        }, { replace: true });
      }
      // #754 — a query carries across the boards that can use it, so moving
      // between Moneyline and Futures keeps the search. Moving to a board that
      // cannot search drops it instead of parking invisible state that would
      // silently re-filter the moment the trader came back.
      //
      // A MODE ends on a move to a board that cannot hold one — Futures,
      // which carries no date for Today or This week to mean anything by. A
      // mode held in reserve there would spring back on return as state
      // nothing on screen had been showing. Moneyline and Spread/Total both
      // hold one, and both admit their events by the same schedule day, so a
      // mode carries between them the way the query does (#754).
      if (!isSearchableMarketType(next)) handleDiscoveryExit();
      // Guarded, like `handleSearchClear`: writing 'manual' over 'manual' is
      // still a reading-slice revision, and one of those discards a pending
      // Reset-undo for nothing.
      else if (!isModeEligibleMarketType(next) && discoveryMode !== 'manual') setDiscoveryMode('manual');
      setSelectedMarketType(next);
    },
    [takeOverFocus, setSelectedMarketType, handleCloseDetail, selectedMarketType,
      selectedSport, defaultDashboardSport, setSearchParams, golfMatchesAvailable,
      handleDiscoveryExit, setDiscoveryMode, discoveryMode],
  );

  // "Today" jumps the calendar back to the actual current day AND
  // enforces single-day mode, regardless of where the user has
  // navigated.
  const handleTodayClick = useCallback(() => {
    takeOverFocus();
    updateReading((previous) => ({
      ...previous, dateAnchor: 'today', dateFilter: 'today', discoveryQuery: '',
      // The toolbar's Today is the SAVED board's today — one sport, the date
      // the trader drives. Choosing it ends the all-sport one.
      discoveryMode: 'manual',
    }));
  }, [takeOverFocus, updateReading]);

  /** The empty state's own escape: put back exactly the two fields
   *  `readingFilterSummary` names, and leave the rest of the reading view
   *  standing. A deliberate board move, so it takes the focus over the same
   *  way every other date-moving control does. */
  const clearNarrowingFilters = useCallback(() => {
    takeOverFocus();
    updateReading((previous) => ({ ...previous, statusFilter: 'all',
      ...(selectedMarketType !== 'series' ? { dateAnchor: 'today' as const } : {}),
    }));
  }, [takeOverFocus, updateReading, selectedMarketType]);


  const handleSportToggle = useCallback(
    (sport: DashboardSport) => {
      takeOverFocus();
      // #728 — the second and last place the saved CFB landing board is read.
      // A CLEAN entry into CFB (the sport chip, arriving from another board)
      // opens on the trader's saved board. Clicking CFB while already on a CFB
      // board is the existing "back to the whole slate" gesture and stays
      // literal: silently rewriting an FCS board to FBS there would read as
      // the toolbar fighting the click, not honouring a preference.
      //
      // Deliberately NOT conditioned on the market type. A bucket key is inert
      // on Futures — `liveGamesForFilter` returns early, the upcoming fallback
      // bails, `selectedCfbBucket` is null and `cfbBoardSelected` is false —
      // so resolving here costs nothing there and means the choice is already
      // in hand when the trader moves to a matchup board. Skipping it instead
      // stranded them: the URL would read a bare 'cfb', which this very
      // condition then treats as "already on CFB", leaving the preference
      // unreachable without leaving the sport.
      //
      // Every other path writes what it names — the bucket chips, All sports,
      // a deep link, a focused market — so once this resolves, `?sport=` means
      // exactly what it says for the rest of the session.
      const target: DashboardSport =
        sport === 'cfb' && (allSports || underlyingSport(selectedDashboardSport) !== 'cfb')
          ? readCfbLandingBoard(authUserId)
          : sport;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sport', target);
          return next;
        },
        { replace: true },
      );
      // A sport switch is a context switch: close the detail panel and
      // collapse the expanded row instead of letting click-time state
      // linger. Without this, a soccer panel survived onto the next sport
      // via the spreadTotalGame fallback (frozen data), and a binary row
      // silently re-expanded when you returned to its sport — and the
      // still-mounted GameDetailPanel paid per-tick renders + held its
      // orderbook subs right through the switch's snapshot burst.
      if (target !== selectedDashboardSport || allSports) {
        handleCloseDetail();
      }
      // Multi-outcome sports (soccer) now have BOTH the 3-way moneyline and the
      // full-game spread/total surfaces, but no futures/series table — so only
      // yank the tab back to moneyline when leaving a 'series' selection that
      // would blank for soccer. Moneyline and spread-total both render, so they
      // carry over on switch, mirroring the binary sports.
      if (MULTI_OUTCOME_SPORTS.has(underlyingSport(target)) && selectedMarketType === 'series'
        && getFutureBoards(underlyingSport(target)).length === 0) {
        setSelectedMarketType('moneyline');
      }
      // #754 — naming a sport is an explicit manual scope gesture, and the
      // all-sport scope a search or a mode took is exactly what it ends.
      handleDiscoveryExit();
    },
    [
      handleDiscoveryExit,
      getFutureBoards,
      setSearchParams,
      selectedDashboardSport,
      allSports,
      handleCloseDetail,
      selectedMarketType,
      takeOverFocus,
      authUserId,
      setSelectedMarketType,
    ],
  );

  const handleAllSportsClick = useCallback(() => {
    takeOverFocus();
    handleDiscoveryExit();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sport', 'all');
        return next;
      },
      { replace: true },
    );
    if (!allSports) handleCloseDetail();
  }, [setSearchParams, allSports, handleCloseDetail, takeOverFocus, handleDiscoveryExit]);

  // ── Saved games section (#120) ─────────────────────────────────────
  // `games` already has the sport / date / status filters applied upstream
  // (getGames), so filtering it here is what makes the Saved section respect
  // the toolbar rather than pierce it: a saved NBA game doesn't appear while
  // you're on the MLB tab, it reappears when you switch back.
  //
  // Every prop except `games` / `titleLabel` is shared with the main table so
  // a saved row behaves identically to its copy below — same pills, same
  // expansion, same trade routing. Spread rather than duplicated so the two
  // tables cannot drift apart.
  const gameTableCommonProps = {
    marketType: selectedMarketType,
    marketSide,
    priceMode,
    expandedGameKey,
    expandedTeamSide: tradeIntent?.side ?? null,
    onToggleExpand: handleToggleExpand,
    // #1389 — NBA rows carry injury entries; their report opens inline in the
    // board and never touches expandedGameKey, tradeIntent or the aim.
    // #1398: only while the reader has injury display turned on.
    injuryEntries: showInjuryPanels,
    onTradeClick: handleUserTradeClick,
    positionByTicker: userActivity.positionByTicker,
    // Stable human display lane. The badge beside the toolbar discloses its
    // vintage; trading, alerts, and automation remain on the strict map.
    ordersByTicker: humanOrdersByTicker,
    botPresenceByTicker: edgeBots.byTicker,
    positionGameKeys,
    usActivityGameKeys: usActivity.gameKeys,
    // #192/#212 — `hiddenEmptySports` is deliberately NOT shared: the two
    // tables disagree about it (Saved is exempt), and a shared default plus a
    // per-site override would silently invert on a prop-order edit. Each
    // GameTable below passes it explicitly. The evidence props stay shared —
    // they're inputs to the predicate, identical for both tables.
    activityReady,
    activeKalshiTickers,
    activePolyMarketIds,
    usQuotes,
    onUsTradeClick: handleUsTradeClick,
    novigQuotes: novigSeam?.quotes ?? null,
    onNovigTradeClick: novigSeam ? handleNovigTradeClick : undefined,
  };

  // ── Reading position (#826, on #829's primitive) ───────────────────
  // Bound to the board's REAL overflow element below, never the window. The
  // context is the row-set identity: anything that changes which rows exist,
  // or their order, makes this a different reading position rather than a
  // stale one to seek back to. A sort inside GameTable is the table's own
  // rearrangement and is deliberately not named here — the anchor is
  // re-resolved by row identity, not by index.
  const readingScrollContext = JSON.stringify([
    allSports ? 'all' : selectedDashboardSport, selectedMarketType, selectedDateIso,
    effectiveSlateRange, dateFilter, statusFilter,
    kalshiMarketsOnlyActive, savedOnlyActive, hiddenEmptySports, minimizedLens != null,
    // #754 — a committed search replaces the row set outright, so a position
    // remembered against the unsearched board is a different reading, not a
    // stale one to seek back to. The page size belongs here for the same
    // reason: showing the rest lengthens the same list.
    discoveryQuery, visibleCount,
  ]);
  const readingScrollRef = useWorkingScroll({
    pane: 'value-dashboard-main',
    instance: readingInstance,
    context: readingScrollContext,
    ready: games.length > 0 || multiEvents.length > 0 || seriesEvents.length > 0,
    // A focused deep link reveals its OWN market. Restoring a remembered
    // position on top of that would fight the reveal, so the pane stays out
    // of it for the whole life of such a URL (#248/#539 keep their behavior).
    enabled: parsedFocus.kind !== 'market',
  });

  /**
   * The empty discovery board, and the way out of it.
   *
   * Named rather than inline because two branches render it: the results list
   * for the families that share one container, and the Spread/Total board,
   * which is mounted from its own slot (below) so that switching into and out
   * of discovery does not remount it.
   */
  const discoveryEmptyState = (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {/* The way out is the gesture that MADE the empty board: a
                    query has one to clear, a mode has a board to go back to.
                    Offering "Clear search" under an unsearched Today — with
                    an empty pair of quotes above it — described a search
                    nobody had run. */}
                <span className="max-w-full break-words text-center [overflow-wrap:anywhere]">
                  {isDiscoveryActive(discoveryQuery)
                    ? <>No markets match “{discoveryQuery}”</>
                    : discoveryEmptyLabel}
                </span>
                <button
                  type="button"
                  onClick={isDiscoveryActive(discoveryQuery)
                    ? handleSearchClear : handleReturnToManual}
                  className="text-primary text-xs underline-offset-2 hover:underline"
                >
                  {isDiscoveryActive(discoveryQuery) ? 'Clear search' : 'Back to my board'}
                </button>
              </div>
  );

  /**
   * #754 — ONE spread/total board, mounted in two places.
   *
   * On the ordinary board it reads its own sport's rows. Under a committed
   * query or a Today/This week mode it is handed the events discovery selected
   * across sports, and everything else about it — trade routing, the one-click
   * intercept, focus resolution, the close signal — is identical. Written once
   * because a second copy of that trade handler is a second place for the
   * execution path to drift.
   */
  const renderSpreadsTotals = (discoveryEvents: readonly GameEventRow[] | null) => (
    <SpreadsTotals
      /* The rows, when discovery chose them. Null is the ordinary board, which
         reads its own — the saved sport, date and bucket props below still
         describe that board either way, because it is what the trader returns
         to. */
      events={discoveryEvents}
      /* Not the board's own name: results span sports, so naming one of them
         over a mixed list is the same false label `titleLabel` fixes for the
         moneyline results table. */
      titleLabel={discoveryEvents ? discoveryTableTitle : undefined}
      sport={selectedSport}
      // #653 — this board reads its own rows, so the CFB bucket has to
      // travel as a prop; the moneyline path filters upstream instead.
      cfbBucket={selectedCfbBucket}
      // #1130 — never over discovery rows, which the moneyline results leave
      // unfiltered too.
      savedOnly={discoveryEvents ? undefined : savedOnlyForSpreadTotal}
      boardLabel={selectedCfbBucketLabel ? sportFilterLabel : undefined}
      loading={boardLoading}
      statusFilter={statusFilter}
      dateFilter={dateFilter}
      selectedDateIso={slateDates.startIso}
      rangeEndIso={slateDates.endIso}
      slateRange={effectiveSlateRange}
      // #757/#248 — a focus query means exact-date, always, for every
      // focus kind. Only the dashboard can see one; whether the view is
      // bounded by a date at all is already in `dateFilter`, which the
      // board has, so it isn't restated here.
      datePinnedByFocus={parsedFocus.kind !== 'none'}
      marketSide={marketSide}
      priceMode={priceMode}
      onActiveGameChange={setSpreadTotalGame}
      onActiveBetLineContextChange={setSpreadTotalBetCtx}
      onTradeClick={(data) => {
        // D-020: armed + entitled Kalshi price clicks submit
        // immediately through the shared intercept (same audited
        // lane, submit-time guard) instead of seeding the ticket.
        // Hydration seeds carry no prefillPrice and can never fire.
        if (
          tryOneClickFromTicketCtx({
            ctx: data,
            surface: 'dash-spread-total',
            period: 'full',
            priceMode,
          })
        ) {
          return;
        }
        // Leaving the hot path on a genuine priced click disarms —
        // the V1 safety boundary, applied only under lt2 (review
        // finding, 2026-08-18): with the flag off these clicks never
        // entered the hot path, and V1 left the page armed here.
        // Price-less seeds (hydration) leave the arm state alone.
        if (lt2Access && oneClickArmed && data.prefillPrice != null) {
          disarmOneClick(ticketClickReason(data.venue));
        }
        // Side panel already opened via onActiveGameChange; this
        // hand-off carries the click's pre-fill (price + side)
        // into the ticket via tradeIntent. The synthesized
        // GameRow has identical away/home books so `teamSide`
        // collapses to 'away' here.
        setTradeIntent({
          side: data.teamSide,
          venue: data.venue,
          prefillPrice: data.prefillPrice,
          initialOrderSide: data.initialOrderSide,
          postOnly: data.postOnly,
          liquiditySeed: data.liquiditySeed ?? data.meta?.liquiditySeed,
          // Tag the prefill with the line it came from. Selecting a
          // different event swaps the panel's game without a fresh
          // click; the stale price is then ignored so the ticket
          // seeds the new book's best bid. See TradeIntent.gameKey.
          gameKey: data.game.key,
        });
        // Page-level YES/NO toggle follows the pill the trader
        // just clicked — green YES pill flips the toolbar to
        // YES so subsequent reads are consistent.
        if (data.initialOrderSide && data.initialOrderSide !== marketSide) {
          setMarketSide(data.initialOrderSide);
        }
      }}
      // #1231 — the US column where the board's sport has US lines.
      showUsColumn={usBoardSports.length > 0 && !allSports}
      onUsTradeClick={handleUsSpreadTotalTradeClick}
      closeSignal={stCloseSignal}
      // #539 — only a LINE target is handed down. A moneyline focus is
      // matched by the hydration hook against `games`; passing its
      // ticker here too would have both paths racing to open a panel.
      // `focusTakenOver` withholds it once the trader has moved the
      // board themselves, matching the moneyline path's behavior.
      focusTicker={lineFocusTarget?.ticker ?? null}
      focusEventIdentity={lineFocusTarget?.eventIdentity ?? null}
      focusVenue={lineFocusTarget?.venue ?? null}
      focusSide={lineFocusTarget?.side}
      onFocusResolution={setLineFocusResolution}
    />
  );

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar row — DashboardToolbar grows to fill the table column;
          PositionsWidget is a fixed w-96 container occupying exactly the
          right column above the order panel (same width, same left
          divider, same right edge). */}
      <div className="border-border flex items-stretch border-b">
        <div className="min-w-0 flex-1 p-3">
          {/* #754 — above the toolbar's own rows, so the query reads as the
              scope everything below it runs inside rather than as one more
              chip. #902's Today/This week controls sit directly below it, and
              the toolbar's own date scope goes visibly inert while one is on:
              two Todays drawn as active at once is the confusion the issue
              forbids. */}
          <div ref={discoveryHeaderRef} className="flex flex-wrap items-start gap-x-3">
          <DiscoverySearchBar
            query={discoveryQuery}
            onCommit={handleSearchCommit}
            onClear={handleSearchClear}
            coverage={discoveryBarCoverage}
            remaining={effectiveDiscoveryMode === 'manual'
              ? discoverySelection?.remaining ?? 0 : 0}
            onShowAll={handleShowAllResults}
            onUseAllDates={selectedMarketType === 'series' && dateFilter !== 'all'
              ? () => setDateFilter('all') : undefined}
            unsupportedNote={discoverySearchable ? null : DISCOVERY_UNSUPPORTED_NOTE}
          />
          {/* #902's controls, directly under the search box and directly above
              the sport chips — the placement the interaction contract asks for
              ("search sits above the related controls; keep Today and This week
              adjacent near sport navigation"). Moneyline only: the futures
              catalog carries no start date, so a mode there would empty the
              board rather than scope it. */}
          {discoveryModeEligible && (
            <div ref={discoveryControlsRef} className="mb-3">
              <DiscoveryModeControls
                mode={effectiveDiscoveryMode}
                /* The range the rows were actually selected over, not the one
                   requested: the component names what it is handed, and the
                   dates it prints must be the dates the list came from. */
                range={discoverySelection?.range ?? discoveryModeRange}
                timeZone={discoveryTimeZone}
                eligibleCount={discoverySelection?.total ?? 0}
                visibleCount={discoverySelection?.visible.length ?? 0}
                coverageSummary={discoveryModeCoverage}
                onModeChange={handleDiscoveryModeChange}
                onShowMore={handleShowAllResults}
                onReturnToManual={handleReturnToManual}
              />
            </div>
          )}
          </div>
          <DashboardToolbar
            selectedSport={selectedDashboardSport}
            selectedSports={selectedDashboardSports}
            activeSports={activeDashboardSports}
            golfMatchesAvailable={golfMatchesAvailable}
            golfMatchBoard={isGolfMatchBoard}
            selectedMarketType={selectedMarketType}
            selectedDate={selectedDate}
            slateRangeLabel={slateRangeLabel}
            isToday={dateFilter === 'today' && slateWindowContains(slateDates, todayIso())}
            discoveryScopeActive={effectiveDiscoveryMode !== 'manual'}
            slateRange={effectiveSlateRange}
            slateRangeEligible={slateRangeEligible}
            onSlateRangeChange={handleSlateRangeChange}
            dateFilter={dateFilter}
            statusFilter={statusFilter}
            marketSide={marketSide}
            priceMode={priceMode}
            showAllSportsChip={showAllSportsChip}
            eligibleEmptySports={isGolfMatchBoard ? [] : eligibleEmptySports}
            hiddenEmptySports={hiddenEmptySports}
            onToggleHiddenEmptySport={toggleHiddenEmptySport}
            onToggleAllEmptySports={toggleAllEmptySports}
            cfbBoardSelected={cfbBoardSelected}
            cfbSavedOnlyEligible={cfbSavedOnlyEligible}
            cfbSavedOnly={cfbSavedOnly}
            onToggleCfbSavedOnly={toggleCfbSavedOnly}
            kalshiMarketsOnlyEligible={isMoneylineBoard}
            kalshiMarketsOnly={kalshiMarketsOnly}
            onToggleKalshiMarketsOnly={toggleKalshiMarketsOnly}
            onSportToggle={handleSportToggle}
            onAllSportsClick={handleAllSportsClick}
            onMarketTypeChange={handleMarketTypeChange}
            onDateChange={handleDateChange}
            onDatePrev={handleDatePrev}
            onDateNext={handleDateNext}
            onDateFilterChange={handleDateFilterChange}
            onTodayClick={handleTodayClick}
            onStatusFilterChange={setStatusFilter}
            onMarketSideChange={setMarketSide}
            onPriceModeChange={setPriceMode}
            onResetView={reading.reset}
            onUndoResetView={reading.undo}
            canUndoResetView={reading.canUndo}
          />
          {humanOrdersByTicker.size > 0 && (
            <QueueVintageBadge status={userActivity.queueStatus} className="mt-2 inline-flex" />
          )}
        </div>
        {/* One-click arm control — its own bordered cell (H-1: fixed slots,
            stacked, so nothing in it or below it moves). */}
        {(oneClickSlot !== 'hidden' || oneClickRevocation.revoked) && !isGolfMatchBoard && (
          <OneClickToolbarCell listener={lt2Access && lt2HotkeysEnabled} revocation={oneClickRevocation} />
        )}
        <div className="border-border w-96 shrink-0 border-l">
          <PositionsWidget
            expanded={showPositionSidebar}
            onToggle={() => setShowPositionSidebar((v) => !v)}
            usRail={usRail}
          />
        </div>
      </div>

      {/* Content row — game table + detail panel side-by-side, both below toolbar */}
      <div className="flex flex-1 overflow-hidden">
        <div
          ref={readingScrollRef}
          data-testid="dashboard-board-pane"
          className="flex-1 overflow-auto p-4"
        >
          {/* #248 — a focused link that resolved to anything other than ONE
              exact market says so here, beside the table, and stays said.
              Not a toast: a trader who followed a link needs to still be able
              to read why no ticket opened after looking away. */}
          {focusHydration.message && (
            <div
              role="status"
              className="border-amber-500/30 bg-amber-500/10 text-amber-200 light:text-amber-700 mb-3 rounded-md border px-3 py-2 text-xs"
            >
              {focusHydration.message}
            </div>
          )}
          {selectedMarketType === 'spread-total' ? (
            /* #754 — ONE slot for this board, discovery or not.
               `renderSpreadsTotals` mounted inside the results container below
               and again in the ordinary branch meant React saw two different
               tree positions: committing a query REMOUNTED the board, its
               expansion state went with the old instance, and the ticket the
               trader had open closed on a gesture the contract says retains it.
               Only the rows differ between the two states, so only the rows
               vary here. (The other families in that container are empty by
               construction on this board — `selectDiscoveryGroups` admits one
               market type.) */
            discoveryResults && discoverySelection?.total === 0
              ? discoveryEmptyState
              : renderSpreadsTotals(discoveryResults ? discoveryResults.lines : null)
          ) : discoveryResults ? (
            /* #754 — a committed search replaces the board's usual single-family
               ladder. Results are routed into EVERY renderer they belong to
               rather than the one the board happened to be on, so a match is
               never dropped for having landed in the other table. The selection
               keeps the store's own order here in Manual; in a Today/This week
               mode it has ranked whole games by traded volume, and
               `preserveGroupOrder` is what stops each table re-sorting that
               ranking away by its own column state. */
            <div className="space-y-4">
              {discoveryResults.moneylines.length > 0 && (
                <GameTable
                  games={discoveryResults.moneylines}
                  viewAnchorSection="discovery"
                  /* NOT the board's own title. Discovery has widened past the
                     selected sport, so "MLB Moneylines — KXMLBGAME" over a
                     table of NBA games is a false label — and the one series
                     ticker the header names cannot describe a cross-sport list
                     either. Which of the two widened it is what the label
                     says: "Search results" over a Today board nobody searched
                     is the same kind of false. */
                  titleLabel={discoveryTableTitle}
                  seriesTicker=""
                  {...gameTableCommonProps}
                  marketType="moneyline"
                  /* Empty-row suppression and the minimized lens are both
                     deliberately absent: a group the trader searched for and
                     matched must not then be hidden by a per-sport preference
                     they set for the ordinary board. */
                  /* The selector already applied the venue filter at group
                     level, so nothing Polymarket-only is smuggled in here. The
                     table's own Kalshi-only pass is a DIFFERENT predicate — it
                     hides a book that is merely stale — and running it over
                     results would hide rows the scope line has already counted,
                     leaving "Showing 20 of 57" describing a shorter list. */
                  kalshiMarketsOnly={false}
                  preserveGroupOrder={effectiveDiscoveryMode !== 'manual'}
                  /* Deliberately NOT lt2MoneylineCardScope. Arming is a mode a
                     trader enters on a board, and results come from sports they
                     did not select; registering these rows would switch an
                     armed-execution path on over markets they never chose. The
                     global A/D readout already reads "aim a game" when nothing
                     is targeted, so the idle state stays truthful. Same
                     per-board judgement the Saved strip makes below. */
                />
              )}
              {discoveryResults.multi.length > 0 && (
                <MultiOutcomeTable
                  events={discoveryResults.multi}
                  seriesTicker=""
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedOutcomeKey={expandedGameKey}
                  onOutcomeClick={handleMultiOutcomeClick}
                  positionByTicker={userActivity.positionByTicker}
                  ordersByTicker={humanOrdersByTicker}
                  /* Kickoff order is this table's own; under a mode the games
                     arrived ranked by traded volume across every sport, and
                     re-sorting them here would silently undo that. */
                  preserveGroupOrder={effectiveDiscoveryMode !== 'manual'}
                />
              )}
              {discoveryResults.futures.map((event) => (
                <SeriesTable key={event.key} event={event} onSelect={handleFutureSelect} />
              ))}
              {discoverySelection?.total === 0 && discoveryEmptyState}
            </div>
          ) : isGolfMatchBoard ? (
            <GolfMatchBoards dateFilter={dateFilter} selectedDateIso={selectedDateIso}
              statusFilter={statusFilter} connected={connected} onSelect={handleGolfSelect} />
          ) : isMultiOutcomeSport ? (
            multiEvents.length > 0 ? (
              <>
                {usingMultiUpcomingFallback && (
                  <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                    No {sportFilterLabel} games on {selectedDate}.{' '}
                    {multiUpcomingFallbackDate
                      ? `Showing the next day with games — ${isoToMdy(multiUpcomingFallbackDate)}.`
                      : 'Showing the next day with games.'}
                  </div>
                )}
                <MultiOutcomeTable
                  events={multiEvents}
                  titleLabel={tableTitleLabel}
                  seriesTicker={seriesTicker}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedOutcomeKey={expandedGameKey}
                  onOutcomeClick={handleMultiOutcomeClick}
                  positionByTicker={userActivity.positionByTicker}
                  ordersByTicker={humanOrdersByTicker}
                />
              </>
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected && boardLoading ? (
                  <>
                      <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                      <span>Loading {sportFilterLabel} events…</span>
                    </>
                ) : connected && feedUnanswered(boardFeed) ? (
                  <FeedUnansweredLine state={boardFeed} sportLabel={baseSportFilterLabel} />
                ) : connected ? (
                  <>
                    <span>No {sportFilterLabel} events</span>
                    <ReadingFilterHint summary={readingFilterSummary} onClear={clearNarrowingFilters} />
                  </>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : selectedMarketType === 'series' ? (
            seriesEvents.length > 0 ? (
              // One board per event — a tennis sport can hold several live
              // tournaments (Wimbledon final + US Open field) whose legs
              // must never co-mingle in a single click-to-trade list.
              <div className="space-y-4">
                {seriesEvents.map((event) => (
                  <SeriesTable key={event.key} event={event} onSelect={handleFutureSelect} />
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected && boardLoading ? (
                  <>
                      <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                      <span>Loading {selectedSport.toUpperCase()} futures markets…</span>
                    </>
                ) : connected && feedUnanswered(boardFeed) ? (
                  <FeedUnansweredLine state={boardFeed} sportLabel={selectedSport.toUpperCase()} />
                ) : connected ? (
                  <>
                    <span>No {selectedSport.toUpperCase()} futures markets</span>
                    <ReadingFilterHint summary={readingFilterSummary} onClear={clearNarrowingFilters} />
                  </>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : (
            /* Saved section sits OUTSIDE the has-games branch on purpose: a
             * saved game whose board row is gone is exactly the one the user
             * needs to remove, and filtering to an empty sport/date must not
             * take the remove control away with it. */
            <>
              {/* Above the Saved section, not below it: the fallback swaps the
                  date for the WHOLE board, Saved table included, so a reader who
                  meets the Saved rows first would otherwise have no reason to
                  doubt they're looking at the date in the toolbar. */}
              {usingUpcomingFallback && upcomingFallbackSlate && (
                <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                  No {sportFilterLabel} games {requestedSlateCopy}.{' '}
                  {upcomingSlateCopy(upcomingFallbackSlate.window, effectiveSlateRange, 'games')}
                </div>
              )}
              {savedRows.length > 0 && (
                <div className="mb-3">
                  <GameTable
                    games={savedRows}
                    viewAnchorSection="saved"
                    // In saved-only mode this table IS the board, so it says so
                    // and names the slice the way the main board would
                    // ("CFB · FBS · Saved (3)"). Outside that mode it stays the
                    // shortlist strip above a full board and keeps its old,
                    // unqualified title.
                    titleLabel={
                      savedOnlyActive
                        ? `${sportFilterLabel} · Saved (${savedRows.length})`
                        : `Saved (${savedRows.length})`
                    }
                    // Blank, not the board's ticker: the header renders it
                    // beside the title, and "Saved — KXMLBGAME" reads as a
                    // claim about the saved set that isn't true of it.
                    seriesTicker=""
                    {...gameTableCommonProps}
                    // #192/#212 — Saved is an explicit user intent, so the
                    // empty-row cleanup never reaches it: the saved matchup
                    // with no book is exactly the one the user needs to see
                    // (and to un-save). The title above counts savedRows
                    // BEFORE GameTable filters, so a filtering Saved table
                    // rendered "Saved (1)" over an empty body.
                    hiddenEmptySports={NO_HIDDEN_EMPTY_SPORTS}
                    // #509 — Saved opts out of the Kalshi filter for the same
                    // reason it opts out of empty-row suppression: a saved
                    // matchup the user deliberately bookmarked must not vanish
                    // because its Kalshi book went quiet. Passed explicitly
                    // rather than relying on the prop default so a future
                    // change to that default cannot silently filter Saved.
                    kalshiMarketsOnly={false}
                    // #654 — Saved opts out of the minimized lens for the third
                    // time on the same principle: a game you deliberately
                    // starred belongs in your shortlist even if you also
                    // minimized it off the full slate. Star and minimize are
                    // opposite verbs, and the shortlist is where star wins.
                    // Omitted, not passed — there is no lens here at all, so no
                    // minimize control appears on these rows either.
                    //
                    // #647 — in saved-only mode this table is the ONLY board, so
                    // the A/D hotkeys move here. The ambiguity that kept them on
                    // the main board (a game present in both tables
                    // double-registering one card id) cannot arise when only one
                    // table is rendered.
                    lt2MoneylineCardScope={
                      savedOnlyActive ? LT2_DASH_ML_CARD_SCOPE : undefined
                    }
                  />
                </div>
              )}
              {savedUnlisted.length > 0 && (
                <SavedUnlistedCard
                  entries={savedUnlisted}
                  slateDates={slateDates}
                  onRemove={(id) => removeSavedGame(id, authUserId)}
                />
              )}
              {savedRows.length === 0 && savedUnlisted.length === 0 && connected && (
                <div className="border-border/60 bg-card/40 text-muted-foreground mb-3 rounded-lg border px-4 py-2 text-xs">
                  {savedCount === 0
                    ? 'No saved games yet — click the bookmark beside a game’s date to keep it here.'
                    : savedCount === 1
                      ? 'Your saved game isn’t on this sport or date.'
                      : `None of your ${savedCount} saved games are on this sport or date.`}
                </div>
              )}
              {/* #654 — the saved-only explainer, in its OWN block rather than
                  inside the all-empty notice above. That notice is additionally
                  gated on `savedUnlisted.length === 0 && connected`, so a user
                  holding a hydra-pruned saved game (or briefly disconnected)
                  would get a hidden board with nothing saying why — the exact
                  unexplained empty state this sentence exists to prevent. It
                  keys only on "saved-only is on and this board has no saved
                  rows", which is the condition that actually needs explaining.
                  The sentence names the control that emptied the board and the
                  bucket it is stacked on ("CFB · FCS"), so composing the two
                  filters cannot produce a silent blank. The turn-off is a
                  button, not a pointer at the toolbar. */}
              {savedOnlyActive && savedRows.length === 0 && (
                <div className="border-border/60 bg-card/40 text-muted-foreground mb-3 rounded-lg border px-4 py-2 text-xs">
                  Saved only is on, so the rest of the {sportFilterLabel} board is hidden.{' '}
                  <button
                    type="button"
                    onClick={toggleCfbSavedOnly}
                    className="text-primary hover:text-primary/80 rounded font-semibold underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                  >
                    Show the full board
                  </button>
                </div>
              )}
              {/* #654 — saved-only hides the full board rather than filtering
                  it down to the saved rows. The Saved table above already IS
                  that list, and it is the better surface for it: it carries the
                  saved games hydra has pruned (SavedUnlistedCard), it refuses to
                  let a quiet book hide a game you starred, and its empty state
                  already distinguishes "nothing saved" from "nothing saved HERE".
                  Filtering the main board instead would reimplement all three
                  beside code that already does them, and render the same rows
                  twice while it did. */}
              {savedOnlyActive ? null : games.length > 0 || archivedLoading ? (
                <GameTable
                  games={games}
                  viewAnchorSection="board"
                  titleLabel={tableTitleLabel}
                  seriesTicker={seriesTicker}
                  {...gameTableCommonProps}
                  hiddenEmptySports={hiddenEmptySports}
                  kalshiMarketsOnly={kalshiMarketsOnlyActive}
                  minimizedLens={minimizedLens}
                  archivedLoading={archivedLoading}
                  // #647: A/D hotkey focus on the standard moneyline rows.
                  // Main board only — wiring Saved too would double-register
                  // a game that is on both (ambiguous focus refuses). The one
                  // exception is saved-only mode, where this table isn't
                  // rendered at all; see the Saved table above.
                  lt2MoneylineCardScope={LT2_DASH_ML_CARD_SCOPE}
                />
              ) : (
                <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                  {connected && boardLoading ? (
                    <>
                      <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                      <span>Loading {sportFilterLabel} games…</span>
                    </>
                  ) : connected && feedUnanswered(boardFeed) ? (
                  <FeedUnansweredLine state={boardFeed} sportLabel={baseSportFilterLabel} />
                ) : connected ? (
                    <>
                    <span>No {sportFilterLabel} games</span>
                    <ReadingFilterHint summary={readingFilterSummary} onClear={clearNarrowingFilters} />
                  </>
                  ) : (
                    <>
                      <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                      <span>Connecting to Hydra...</span>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail panel — sibling of the table, both below the toolbar.
         *  Key off the active game so switching between moneyline and
         *  spread/total expansions remounts the panel cleanly. */}
        {!discoveryActive && isGolfMatchBoard && golfSelection ? (
          <StandaloneMarketDetailPanel selection={golfSelection} onClose={handleCloseDetail} />
        ) : selectedMarketType === 'series' && futuresSelection && futureSelectionVisible ? (
          <FuturesDetailPanel selection={futuresSelection} onClose={handleCloseDetail} />
        ) : (!isGolfMatchBoard || discoveryActive) && selectedMarketType !== 'series' && expandedGame && (
          <ExpandedGamePanel
            game={expandedGame}
            onClose={handleCloseDetail}
            tradeIntent={tradeIntent}
            marketSide={marketSide}
            onTeamSideChange={(side) => {
              setTradeIntent((t) => (t ? { ...t, side } : null));
            }}
            betLineContext={
              // Identity check for spread/total (the synth is pushed and held
              // by reference); multi-outcome synths are re-derived fresh each
              // tick, so match on the key instead.
              spreadTotalGame === expandedGame || expandedGame.key.includes('|multi|')
                ? spreadTotalBetCtx
                : null
            }
          />
        )}
      </div>

      {/* The bottom bar (display toggles + Hydra feed inspection) was promoted
          to the global <StatusBar/> in AuthShell — content-width, on every
          screen, fused with the rate-limit/tier account cluster. */}

      {/* Position popup — fixed-position overlay, can coexist with the
          detail panel (used to fight it for the right rail). */}
      {showPositionSidebar && (
        <PositionSidebar
          usRail={usRail}
          onClose={() => setShowPositionSidebar(false)}
          onOpenDetail={
            onOpenPositionsPage
              ? () => {
                  setShowPositionSidebar(false);
                  onOpenPositionsPage();
                }
              : undefined
          }
        />
      )}

      {/* Trade modal removed 2026-05-22 (files deleted 2026-07-10). All
          trade entry now happens inside the GameDetailPanel side panel
          rendered above. */}
    </div>
  );
}

/** Thin wrapper around GameDetailPanel that pulls the Unabated
 *  consensus from the batched gameStates poll and threads it into the
 *  panel's `unabated` prop. Extracted so the `useGameStateFor` hook
 *  call has a stable mount lifetime — putting the hook directly in
 *  ValueDashboard would mean it runs on every render whether or not
 *  any panel is open. Same swap-by-team-code logic the GameDetail page
 *  uses: Unabated's away/home can disagree with the canonical alpha-
 *  sorted GameRow, so we align by team code before mapping.
 *  Exported: PositionTracker mounts the same panel as its right-rail
 *  buy window so both surfaces share one ticket + consensus wiring. */
export function ExpandedGamePanel({
  game,
  onClose,
  tradeIntent,
  marketSide,
  onTeamSideChange,
  betLineContext,
}: {
  game: GameRow;
  onClose: () => void;
  tradeIntent: TradeIntent | null;
  marketSide: MarketSide;
  onTeamSideChange: (side: TeamSide) => void;
  betLineContext: BetLineContext | null;
}) {
  // For spread/total synth keys (`<eventKey>|spread|<ticker>`) we want
  // the eventKey portion for the gameStates lookup, not the synth key
  // itself. Moneyline rows fall through unchanged. GameOpenOrders' order
  // matching reaches the same boundary through `orderScopeGameKey`, which
  // applies `baseGameKey` first.
  const lookupBase = baseGameKey(game.key);
  // Live (doubleheader-safe) key — see the consensus note below; the poll and
  // the WS consensus must agree on the v2 key or a doubleheader's two rows
  // share one score.
  const hydraKey = toHydraGameKeyLive(lookupBase);
  const gameState = useGameStateFor(hydraKey);
  // Prefer the live WS consensus (sub-second freshness via the `unabated`
  // channel, push on ≥1¢ shift). Fall back to the 5s gameStates poll
  // snapshot for cold-start and any game outside hydra's active consensus set.
  // Consensus is cached per the doubleheader-safe v2 key, which IS the row key,
  // so pass `lookupBase` (the row key minus any synth suffix) straight through —
  // a doubleheader's two games then read their own un-blended consensus.
  const consensusKey = lookupBase || null;
  const liveConsensus = useConsensusFor(consensusKey);
  const liveConsensusTrend = useConsensusTrendFor(consensusKey);
  // OP odds for the panel's "O" row — same key vocabulary as consensus,
  // separate store map. Gated on the user toggle so switching showOpOdds
  // off hides the row along with the dashboard column.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const opSlice = useOpOddsFor(showOpOdds ? consensusKey : null);
  // The ONE fair-view builder (rules 4 + 6c) — no inline copy of the swap
  // rule, no local fair-value math. Null (neither consensus nor anchor
  // fair on the frame) hides the row.
  const opLastAnchor = useOpLastAnchorFairFor(showOpOdds ? consensusKey : null);
  const opOdds = opSlice ? buildOpFairView(opSlice, game.homeCode, opLastAnchor) : null;
  const opOddsTrend = useOpTrendFor(showOpOdds ? consensusKey : null);
  // odds-gamestate-feed-filters D6: the "O" row draws this game's full-game
  // moneyline, and only on a plain ticket (a line / ladder ticket hides it).
  // A moneyline board facet for the game's sport — the D2 board shape, never
  // the whole game — keeps it fed where no board declares one (the Position
  // Tracker's buy panel); on the dashboard it sits inside the board's own.
  const ticketOddsId = useId();
  useOddsFeedNeeds(`ticket-odds:${ticketOddsId}`, showOpOdds && consensusKey && betLineContext == null
    ? { board: { sports: game.sport ? [game.sport] : 'all', marketTypes: ['moneyline'], periods: ['full'] } }
    : null);
  const unabated = (() => {
    // WS is now the sole source of consensus — hydra moved it off the REST
    // gameStates response as of a37c761. Snapshot's durable last-known cache
    // fills in stable pre-game markets that don't actively shift.
    const c = liveConsensus;
    if (!c) return null;
    // Same swap rule the GameDetail page applies — Unabated's awayTeam
    // can land in our homeCode slot after canonical-key alpha-sort.
    // swapVenueColumns propagates the same flip to K and P rows so all
    // three columns line up under the matching team-code headers.
    const swapped = c.awayTeam === game.homeCode;
    return {
      awayCents: swapped ? c.homeFairProbCents : c.awayFairProbCents,
      homeCents: swapped ? c.awayFairProbCents : c.homeFairProbCents,
      // The order-entry U row displays the tenth-accurate value when hydra
      // sends it (integer cents remain the fallback + the glow key).
      awayTenths: (swapped ? c.homeFairProbTenths : c.awayFairProbTenths) ?? null,
      homeTenths: (swapped ? c.awayFairProbTenths : c.homeFairProbTenths) ?? null,
      // Sub-cent trend + sharp anchor for the U row's glow treatments. The
      // trend keeps Unabated's own slots; the panel re-applies `swapped`.
      trend: liveConsensusTrend,
      swapped,
      sharpBookUsed: c.sharpBookUsed ?? null,
    };
  })();
  return (
    <GameDetailPanel
      key={game.key}
      game={game}
      onClose={onClose}
      tradeIntent={tradeIntent}
      marketSide={marketSide}
      onTeamSideChange={onTeamSideChange}
      betLineContext={betLineContext}
      unabated={unabated}
      opOdds={opOdds}
      opOddsTrend={opOddsTrend}
    />
  );
}

// Saved games that have no row on the board (#120). Rendered from persisted
// metadata ONLY — teams, date, sport. No price, no status, no book: a saved
// snapshot must never read as a live quote, so there is deliberately nothing
// here that could be mistaken for one. The bookmark is filled and removes the
// entry, which is what keeps an expired game removable.
function SavedUnlistedCard({
  entries,
  slateDates,
  onRemove,
}: {
  entries: SavedGame[];
  /** The window the board is showing, so the per-entry note can tell "gone
   *  from a window that covers it" apart from "simply outside it" (#712). */
  slateDates: SlateWindow;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="border-border bg-card mb-3 rounded-lg border px-4 py-2">
      <div className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase tracking-wider">
        Saved — not on this board
      </div>
      <ul>
        {entries.map((e) => (
          <li key={e.id} className="flex items-center gap-2 py-1 text-xs">
            <button
              type="button"
              onClick={() => onRemove(e.id)}
              aria-label={`Remove ${e.awayCode} @ ${e.homeCode} from saved games`}
              title="Remove from Saved games"
              className="text-foreground shrink-0 rounded p-0.5 transition-colors hover:text-red-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              <Bookmark size={12} className="fill-current" />
            </button>
            <span className="text-muted-foreground w-20 shrink-0 font-mono">{e.date}</span>
            <span className="text-foreground">
              {e.awayCode} <span className="text-muted-foreground/40">@</span> {e.homeCode}
            </span>
            <span className="text-muted-foreground/60 uppercase">{e.sport}</span>
            {/* Two very different situations wear the same "no row on the
                board" symptom, and conflating them would misreport a healthy
                game as gone: a saved game for ANOTHER date is simply off-screen,
                whereas one whose own date is showing really has dropped out of
                both the live feed and the analytics window. */}
            <span className="text-muted-foreground/50 ml-auto italic">
              {slateWindowContains(slateDates, e.date)
                ? 'no longer listed'
                : slateDates.startIso === slateDates.endIso
                  ? 'not on this date'
                  : 'not in this range'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Project an analytics archived game into the GameRow shape the table
// already consumes. Most fields are stub-empty — the renderer branches
// on `archived` and skips the live-row plumbing entirely. `key` is the
// canonical hydra gameKey (date|TEAM_A|TEAM_B), which is also what
// positionGameKeys is keyed on, so the archived row's position-highlight
// lookup works without any extra translation.
function archivedToGameRow(a: ArchivedGame): GameRow {
  return {
    key: a.gameKey,
    sport: a.sport as Sport,
    date: a.date,
    timePt: '',
    startTimeMs: null,
    gameState: 'final',
    finalWinnerSide: null,
    awayCode: a.teamA,
    homeCode: a.teamB,
    awayName: a.teamA,
    homeName: a.teamB,
    kalshi: null,
    polymarket: null,
    archived: {
      kalshiVolume: a.kalshiVolume,
      polyVolume: a.polyVolume,
    },
  };
}
